import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { ApprovalInstance, ApprovalStepInstance, ResolvedApprovalStep, ApprovalActionResult, FormFieldConfig } from '../models/approval-flow.model';
import { ApprovalService } from '../services/approval.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, finalize } from 'rxjs/operators';
import { of, firstValueFrom } from 'rxjs';
import { UserService } from '../services/user.service';
import { FormSignatureComponent } from '../../../items/form-signature.component';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { I18nService } from '../../../../services/i18n.service';
import { EmailService } from '../../../../services/email.service';
// 導入表單字段渲染器
import { FormFieldRendererComponent } from '../../../shared/form-field-renderer/form-field-renderer.component';
import { FieldRenderMode } from '../../../shared/form-field-renderer/form-field-renderer.types';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../services/global-environment';
import { AuthService } from '../../../../services/auth.service';
import { SignatureData } from '../../../../models/signature.model';
import { ApprovalWorkflowConfigService } from '../../../../services/approval-workflow-config.service';
import { ApprovalWorkflowConfig } from '../../../../models/approval-workflow-config.model';
import { AssistantUrlService } from '../../../../services/assistant-url.service';

// 定義表單欄位配置介面
// export interface FormFieldConfig {
//   type: string;
//   label: string;
//   id: string;
//   name: string;
//   placeholder?: string;
//   required?: boolean;
//   options?: { value: string; label: string }[];
// }

// Add interface extension for action result
interface ApprovalStepInstanceWithAction extends ApprovalStepInstance {
  actionResult?: {
    success: boolean;
    message: string;
  } | null;
}

@Component({
  selector: 'app-approval-status',
  templateUrl: './approval-status.component.html',
  standalone: true,
  styleUrls: ['./approval-status.component.scss'],
  imports: [CommonModule, FormsModule, FormSignatureComponent, TranslatePipe, FormFieldRendererComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApprovalStatusComponent implements OnInit, OnChanges {
  @Input() id: string = "";
  @Input() formId: string = "";
  @Input() stepDefinitionId: string = ""; //送入目前簽核流程的id
  @Input() approvalFlow: ResolvedApprovalStep[] = []; // 新增：簽核流程資料
  @Input() stepIndex: number = -1; // 新增：指定要顯示的階段索引，-1表示顯示所有階段
  @Input() approvalResult: any = null; // 新增：簽核結果資料，包含歷史資料
  @Output() approvalComplete = new EventEmitter<ApprovalInstance>();
  // @Output() approvalActionCompleted = new EventEmitter<ApprovalActionResult>(); // 新增：更詳細的簽核動作完成事件

  @Input() showCommentField: boolean = true;  // 是否顯示意見欄位
  @Input() showSignatureField: boolean = true; // 是否顯示簽名欄位
  @Input() signatureRequired: boolean = false;

  approvalInstance: ApprovalInstance | null = null;
  loading = false;
  error = false;
  comment = '';
  signature: SignatureData | null = null;
  actionInProgress = false;
  currentUserId: string = '';
  currentUserName: string = ''; // 新增：目前使用者名稱
  currentUserInfo: any = null; // 新增：目前使用者完整資訊

  // 新增：自訂表單相關屬性
  customFormValidation: { [stepIndex: number]: boolean } = {}; // 每個步驟的表單驗證狀態
  customFormData: { [stepIndex: number]: any } = {}; // 每個步驟的表單資料

  // 新增：表單渲染模式枚舉（供模板使用）
  public FieldRenderMode = FieldRenderMode;

  // 新增：測試模式開關（用於放寬權限檢查）
  isTestMode: boolean = true; // 設為 true 以進行測試

  // 新增：退回相關狀態
  showRejectOptions = false;
  currentRejectStepIndex: number | null = null;
  rejectFormData = {
    rejectType: 'complete_reject' as 'complete_reject' | 'return_to_step' | 'return_to_submitter',
    returnToStepId: '',
    rejectReason: ''
  };
  availableReturnSteps: any[] = [];

  // 新增：歷史記錄追蹤
  approvalHistory: any[] = [];

  // 🆕 新增：簽核流程配置
  approvalWorkflowConfig?: ApprovalWorkflowConfig;

  constructor(
    private approvalService: ApprovalService,
    private userService: UserService,
    private router: Router,
    private i18nService: I18nService,
    private emailService: EmailService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private approvalWorkflowConfigService: ApprovalWorkflowConfigService,  // 🆕 新增
    private assistantUrlService: AssistantUrlService  // 🆕 新增
  ) {}

  async ngOnInit() {
    console.log("=== ngOnInit 開始 ===");
    console.log("初始 signature 值:", this.signature);
    console.log("showSignatureField:", this.showSignatureField);
    console.log("signatureRequired:", this.signatureRequired);

    // 🆕 載入簽核流程配置
    await this.loadApprovalWorkflowConfig();

    this.getCurrentUserId();
    this.getCurrentUserInfo();
    this.loadApprovalStatusIfNeeded();

    console.log("ngOnInit - stepDefinitionId:", this.stepDefinitionId);
    console.log("ngOnInit - All Input Values:", {
      formId: this.formId,
      stepDefinitionId: this.stepDefinitionId,
      stepIndex: this.stepIndex,
      approvalFlow: this.approvalFlow
    });

    console.log("=== ngOnInit 結束 ===");

    // 測試模式下，如果沒有 approvalFlow 資料，創建示例資料
    // if (this.isTestMode && this.approvalFlow.length === 0) {
    //   this.createTestApprovalFlow();
    // }
  }  ngOnChanges(changes: SimpleChanges) {
    console.log("ngOnChanges triggered with changes:", changes);

    if (changes['formId']) {
      console.log("formId changed:", changes['formId'].currentValue);
      this.loadApprovalStatusIfNeeded();
    }
    if (changes['approvalFlow'] && this.approvalFlow.length > 0) {
      console.log("approvalFlow changed:", changes['approvalFlow'].currentValue);
      this.createApprovalInstanceFromFlow();
    }
    if (changes['stepIndex'] || changes['approvalResult']) {
      console.log("stepIndex or approvalResult changed");
      // 當stepIndex或approvalResult變更時，重新處理顯示邏輯
      this.cdr.markForCheck();
    }
    if (changes['stepDefinitionId']) {
      // 當stepDefinitionId變更時，記錄日誌並標記重新檢測
      console.log("stepDefinitionId changed from:", changes['stepDefinitionId'].previousValue, "to:", changes['stepDefinitionId'].currentValue);
      this.cdr.markForCheck();
    }
  }

  getCurrentUserId() {
    this.currentUserId = this.userService.getCurrentUserId();
  }

  getCurrentUserInfo() {
    // 取得目前使用者的詳細資訊
    this.userService.getCurrentUserDetails().subscribe({
      next: (userInfo: any) => {
        this.currentUserInfo = userInfo;
        this.currentUserName = userInfo?.username || this.currentUserId;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.warn('無法取得使用者資訊:', error);
        this.currentUserName = this.currentUserId;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * 🆕 載入簽核流程配置
   */
  private async loadApprovalWorkflowConfig(): Promise<void> {
    try {
      const companyid = environment.companyid;
      this.approvalWorkflowConfig = await this.approvalWorkflowConfigService.getConfig(companyid);
      // Service 中已有完整的 log，這裡不需要重複
    } catch (error) {
      // 錯誤已在 service 中處理，會使用預設值
      // 不需要額外 log，避免造成誤解
    }
  }

  loadApprovalStatusIfNeeded() {
    if (this.approvalFlow.length > 0) {
      // 如果有傳入簽核流程資料，使用該資料建立簽核實例
      this.createApprovalInstanceFromFlow();
    } else if (this.formId) {
      this.loadApprovalStatus();
    } else {
      this.formId = 'test_form_1';
      this.loadApprovalStatus();
    }
  }

  loadApprovalStatus() {
    this.loading = true;
    this.error = false;

    this.approvalService.getApprovalForForm(this.formId)
      .pipe(
        catchError(error => {
          console.error('Error loading approval status', error);
          this.error = true;
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(instance => {
        if (instance) {
          this.approvalInstance = instance;
        }
        this.cdr.markForCheck();
      });
  }

  isCurrentApprover(stepIndex: number): boolean {
    if (!this.approvalInstance) return false;

    const actualIndex = this.getActualStepIndex(stepIndex);

    // 測試模式：允許當前用戶簽核所有步驟
    if (this.isTestMode) {
      // 單一階段模式時，檢查該階段是否為進行中狀態
      if (this.stepIndex >= 0) {
        const stepStatus = this.getStepDisplayStatus(stepIndex);
        return stepStatus === 'in_progress' || stepStatus === 'pending';
      }
      // 一般模式時，檢查是否為當前步驟
      return actualIndex === this.approvalInstance.currentStepIndex;
    }

    // 單一階段模式時，不需要檢查是否為當前步驟，只需檢查狀態和權限
    if (this.stepIndex >= 0) {
      const stepStatus = this.getStepDisplayStatus(stepIndex);
      if (stepStatus !== 'in_progress' && stepStatus !== 'pending') {
        return false; // 已完成的階段不可操作
      }
    } else {
      // 一般模式時，檢查當前步驟是否為進行中狀態
      if (actualIndex !== this.approvalInstance.currentStepIndex) return false;
    }

    // 如果使用新的簽核流程資料結構，檢查目前使用者是否在可簽核人員列表中
    if (this.approvalFlow.length > 0) {
      const isInList = this.isCurrentUserInApproverList(actualIndex);
      return isInList;
    }

    // 原本的邏輯：檢查當前步驟和使用者ID
    const currentStep = this.approvalInstance.steps[actualIndex];
    const isOriginalApprover = currentStep.approver.userId === this.currentUserId;
    return isOriginalApprover;
  }

  // 處理簽名變更
  onSignatureChange(value: SignatureData | string | null): void {
    console.log("=== onSignatureChange 被觸發 ===");
    console.log("接收到的 value:", value);
    console.log("value 的類型:", typeof value);
    console.log("當前的 signature:", this.signature);

    if (typeof value === 'string') {
      // 兼容舊格式（純字串）
      this.signature = value ? { imageData: value } : null;
      console.log("已轉換為新格式 - signature:", this.signature);
    } else {
      // 新格式（SignatureData 物件）
      this.signature = value;
      console.log("直接使用新格式 - signature:", this.signature);
    }

    // 手動觸發變更檢測
    this.cdr.markForCheck();

    console.log("=== onSignatureChange 處理完成 ===");
  }

  canPerformAction(): boolean {
    if (this.signatureRequired && (!this.signature || !this.signature.imageData)) {
      return false;
    }
    return true;
  }

  approve(stepIndex: number) {


    // 檢查簽名要求
    // if (this.signatureRequired && !this.signature) {
    //   const currentStep = this.approvalInstance!.steps[stepIndex] as ApprovalStepInstanceWithAction;
    //   currentStep.actionResult = {
    //     success: false,
    //     message: this.i18nService.translate('approval.status.required_fields_error')
    //   };
    //   return;
    // }

    // 暫時移除自訂表單驗證阻擋，讓功能更穩定
    // 未來可以根據需要重新啟用
    /*
    if (!this.isCustomFormValid(stepIndex)) {
      const currentStep = this.approvalInstance!.steps[stepIndex] as ApprovalStepInstanceWithAction;
      currentStep.actionResult = {
        success: false,
        message: '請完成所有必填的自訂表單欄位'
      };
      return;
    }
    */

    // 執行簽核動作
    this.performAction('approve', stepIndex);

    //TODO:這個要移到 display的方法中
    // 簽核完成後，檢查下一關並發送通知郵件
    // this.sendNotificationToNextApprovers(stepIndex);
  }

  reject(stepIndex: number) {
    // 不直接執行拒絕，而是顯示退回選項
    this.currentRejectStepIndex = stepIndex;
    this.initializeRejectOptions(stepIndex);
    this.showRejectOptions = true;
  }

  performAction(action: 'approve' | 'reject', stepIndex: number) {

 console.log("this.stepDefinitionId",this.stepDefinitionId);
 console.log("this.approvalInstance",this.approvalInstance);

    if(!this.stepDefinitionId || !this.approvalInstance) return;

    // 使用 stepDefinitionId 找到對應的步驟
    const currentStep = this.approvalInstance.steps.find(step => step.stepDefinitionId === this.stepDefinitionId) as ApprovalStepInstanceWithAction;

    if (!currentStep) {
      console.error('找不到對應的步驟定義ID:', this.stepDefinitionId);
      return;
    }

    this.actionInProgress = true;
    // 重置該步驟的操作結果
    currentStep.actionResult = null;

    console.log("currentStep", currentStep);

    // 更新步驟資料，包含使用者填寫的內容
    currentStep.status = action === 'approve' ? 'approved' : 'rejected';
    currentStep.comment = this.comment || '';
    currentStep.signature = this.signature?.imageData || null;
    currentStep.actionDate = new Date();

    // 取得實際的步驟索引
    const actualStepIndex = this.getActualStepIndex(stepIndex);

    // 如果有自訂表單資料，也要包含進去
    if (this.customFormData[actualStepIndex]) {
      (currentStep as any).customFormData = this.processCustomFormDataForSubmit(actualStepIndex);
    }

    // 設置操作結果
    const actionText = action === 'approve' ? this.i18nService.translate('approval.status.approve') : this.i18nService.translate('approval.status.reject');
    currentStep.actionResult = {
      success: true,
      message: this.i18nService.translate('approval.status.action_completed', { action: actionText })
    };

    // 更新整個 approvalInstance 的狀態
    if (this.approvalInstance) {

      this.approvalInstance.stepDefinitionId  = this.stepDefinitionId;

      // 根據退回類型決定流程狀態
      if (action === 'reject') {
        // 使用退回表單數據決定狀態
        const rejectType = (currentStep as any).rejectType || 'complete_reject';

        switch (rejectType) {
          case 'complete_reject':
            this.approvalInstance.status = 'rejected';
            this.approvalInstance.completedAt = new Date();
            break;

          case 'return_to_step':
          case 'return_to_submitter':
            (this.approvalInstance as any).status = 'returned';
            // 不設置 completedAt，因為流程還未結束
            break;
        }
      } else if (action === 'approve') {
        // 檢查是否還有下一步驟
        const actualStepIndex = this.getActualStepIndex(stepIndex);
        const nextStepIndex = actualStepIndex + 1;

        if (nextStepIndex < this.approvalInstance.steps.length) {
          // 還有下一步，流程繼續
          this.approvalInstance.currentStepIndex = nextStepIndex;
          this.approvalInstance.steps[nextStepIndex].status = 'in_progress';
        } else {
          // 最後一步，流程完成
          this.approvalInstance.status = 'approved';
          this.approvalInstance.completedAt = new Date();
        }
      }

      // 更新時間戳記
      this.approvalInstance.updatedAt = new Date();
    }

    console.log("完整的簽核資料:", {
      stepDefinitionId: currentStep.stepDefinitionId,
      action: action,
      status: currentStep.status,
      comment: currentStep.comment,
      signature: currentStep.signature,
      customFormData: (currentStep as any).customFormData,
      actionDate: currentStep.actionDate,
      approvalInstanceStatus: this.approvalInstance?.status
    });

    // 將完整的 approvalInstance 送回父元件
    if (this.approvalInstance) {
      // 建立詳細的動作結果物件
      const actionResult: ApprovalActionResult = {
        stepDefinitionId: currentStep.stepDefinitionId,
        action: action,
        status: currentStep.status as 'approved' | 'rejected',
        comment: currentStep.comment || '',
        signature: currentStep.signature || null,
        customFormData: (currentStep as any).customFormData || {},
        actionDate: currentStep.actionDate!,
        approver: {
          userId: this.currentUserId,
          userName: this.currentUserName,
          roleId: currentStep.approver.roleId,
          roleName: currentStep.approver.roleName,
          departmentId: currentStep.approver.departmentId,
          departmentName: currentStep.approver.departmentName
        },
        approvalInstance: this.approvalInstance
      };



    // 提取當前簽核階段的資料
    const currentStepData = this.extractCurrentStepData(this.approvalInstance);

    // ✅ 提交簽核，並傳入動作類型
    this.submitApproval(currentStepData, action);




    }

    // ⚠️ 不要在這裡清空表單！
    // 簽核視窗會在成功後 1.5 秒關閉（見 approval-flow-display.component.ts 的 handleApprovalSuccess）
    // 在視窗關閉前清空表單會導致簽名欄位重新渲染，造成閃現問題
    // 讓元件自然銷毀即可，不需要手動清空

    // 重置狀態標記
    this.actionInProgress = false;

    // 🔥 關鍵修正：分離變更檢測，避免父元件更新觸發重新渲染
    // 簽核提交後，暫時停止變更檢測，避免簽名欄位閃現
    // 視窗會在 1.5 秒後自動關閉並銷毀元件
    this.cdr.detach();

    console.log('🔒 已停止變更檢測，避免簽名欄位重新渲染');
  }


   /**
   * 提取當前簽核階段的資料
   */
  private extractCurrentStepData(approvalData: any): any {

    console.log('提取當前簽核階段資料:', approvalData);

    try {
      // 找到當前簽核的步驟
      const currentStep = approvalData.steps.find((step: any) =>
        step.stepDefinitionId === approvalData.stepDefinitionId // 或根據步驟定義ID
      );

      console.log('當前簽核步驟:', currentStep);

      if (!currentStep) {
        console.error('找不到當前簽核步驟');
        return null;
      }

      console.log('this.approvalFlow:', this.approvalFlow);

      // 找到對應的原始簽核流程定義
      const originalStep = this.approvalFlow.find(
        (originalStep: any) => originalStep.stepDefinitionId === currentStep.stepDefinitionId
      );

      // 修正 approver.userName 和 userId - 使用當前登入使用者的資訊
      const currentUserInfo = this.authService.getUserInfo();
      let correctedApprover = {
        ...currentStep.approver,
        userName: currentUserInfo.username,
        userId: currentUserInfo.userid
      };

      // 整合 customFormData 到 customFormItems 中
      let integratedCustomFormItems = originalStep?.customFormItems || [];
      if (currentStep.customFormData && integratedCustomFormItems.length > 0) {
        integratedCustomFormItems = integratedCustomFormItems.map((item: any) => {
          const formDataValue = currentStep.customFormData[item.name] || currentStep.customFormData[item.id];
          return {
            ...item,
            value: formDataValue !== undefined ? formDataValue : item.value
          };
        });
      }

      // 整合當前步驟的完整資料
      const integratedCurrentStep = {
        // 保留簽核執行結果的資料
        stepDefinitionId: currentStep.stepDefinitionId,
        name: currentStep.name,
        order: currentStep.order,
        approver: correctedApprover, // 使用修正後的 approver 資訊
        status: currentStep.status,
        comment: currentStep.comment,
        signature: currentStep.signature,
        actionDate: currentStep.actionDate,
        actionResult: currentStep.actionResult,

        // 整合原始資料庫格式的資料
        level: originalStep?.level || currentStep.order,
        stepName: originalStep?.stepName || currentStep.name,
        approverType: originalStep?.approverType ,
        customFormItems: integratedCustomFormItems, // 使用整合後的自訂表單項目

        // 其他可能的原始欄位 (都很有參考價值，但目前都用不到，先註解起來)
        ...(originalStep && {
          // timeLimit: originalStep.timeLimit,
          // isParallel: originalStep.isParallel,
          // requiredAll: originalStep.requiredAll,
          // description: originalStep.description,
          // formConfig: originalStep.formConfig,
          // requireComment: originalStep.requireComment,
          // requireSignature: originalStep.requireSignature,
          // optional: originalStep.optional,
          // userId: originalStep.userId,
          // userName: originalStep.userName,
          // isDynamicDepartment: originalStep.isDynamicDepartment,
          approverList: originalStep.approverList
        })
      };

      // 取得下一個步驟的名稱
      const nextStepIndex = this.stepIndex + 1;
      let nextStepName = "";
      let nextStepid = "";

      if (nextStepIndex < this.approvalFlow.length) {
        const nextStep = this.approvalFlow[nextStepIndex];
        nextStepName = nextStep?.stepName || "";
        nextStepid = nextStep?.stepDefinitionId || "";
      }

      // 🔑 新增：包含歷史記錄的簽核資料
      const currentStepData = {
        id: approvalData.id,
        flowDefinitionId: approvalData.flowDefinitionId,
        formId: approvalData.formId,
        formTypeId: approvalData.formTypeId,
        initiator: approvalData.initiator,
        status: approvalData.status,
        currentStepIndex: this.stepIndex,
        updated_step: integratedCurrentStep, // 只包含當前步驟
        all_steps: approvalData.steps, // ⭐ 新增：包含所有步驟的完整陣列（用於退回操作的狀態重置）
        next_step_name: nextStepName, //下一個步驟的名稱
        next_step_stepDefinitionId : nextStepid, //下一個步驟的ID
        // 🔑 新增：包含歷史記錄資料
        history: (approvalData as any).history || this.approvalHistory || [],
        createdAt: approvalData.createdAt,
        updatedAt: approvalData.updatedAt
      };

      return currentStepData;
    } catch (error) {
      console.error('提取當前簽核階段資料時發生錯誤:', error);
      return null;
    }
  }


  /**
   * 提交簽核到後端
   * @param approvalData 簽核資料
   * @param action 簽核動作類型：'approve' 同意、'reject' 拒絕、undefined 不發送通知（向下相容）
   */
  private submitApproval(approvalData: any, action?: 'approve' | 'reject'): void {
    if (!this.formId) {
      console.error('缺少表單ID，無法提交簽核');
      return;
    }

    const formData = {
      id: this.id, // 資料本身的ID
      formId: this.formId,
      stepIndex: this.stepIndex,
      approvalData: approvalData
    };

    console.log('提交簽核資料到後端:', formData, '動作類型:', action);

    this.approvalService.submitApprovalAndUpdateForm(formData).subscribe({
      next: (response) => {
        console.log('簽核提交成功:', response);

        // 🔧 更新前端的 approvalFlow 資料：設定當前步驟的 approver 資訊
        const actualIndex = this.getActualStepIndex(this.stepIndex);
        if (this.approvalFlow && this.approvalFlow[actualIndex]) {
          const currentStep: any = this.approvalFlow[actualIndex];

          // 確保有效的使用者資訊 - 使用 AuthService 取得使用者資訊
          const userInfo = this.authService.getUserInfo();
          const effectiveUserId = this.currentUserId || this.userService.getCurrentUserId() || userInfo.userid || '';
          const effectiveUserName = this.currentUserName || userInfo.username || '';

          // 更新 approver 物件
          currentStep.approver = {
            userId: effectiveUserId,
            userName: effectiveUserName,
            id: effectiveUserId,
            name: effectiveUserName
          };

          console.log('✅ 已更新前端 approvalFlow 的 approver 資訊:', {
            stepIndex: actualIndex,
            approver: currentStep.approver
          });
        }

        // ✅ 根據動作類型決定通知邏輯
        if (action === 'approve') {
          // 同意時：通知下一關簽核人員
          console.log('簽核同意，發送通知給下一關簽核人員');
          this.sendNotificationToNextApprovers(this.stepIndex);
        } else if (action === 'reject') {
          // 拒絕時：通知受影響人員
          console.log('簽核拒絕，發送拒絕通知給受影響人員');
          this.sendRejectNotification(this.stepIndex);
        } else {
          // action 為 undefined：不發送任何通知（向下相容舊有呼叫）
          console.log('未指定動作類型，不發送通知');
        }

         //將資料回送給display元件，但很多東西這邊都做完了，那邊直接往後關閉視窗跟調整顯示即可
          this.approvalComplete.emit(response); // 向後相容的事件

      },
      error: (error) => {
        console.error('簽核提交失敗:', error);
        this.approvalComplete.emit(error); // 向後相容的事件
      },
      complete: () => {
      }
    });
  }

  /**
   * 調試用：檢查簽核操作區域顯示條件
   */
  debugApprovalAreaConditions(stepIndex: number): void {
    const actualIndex = this.getActualStepIndex(stepIndex);
    const isReadOnly = this.isStepReadOnly(stepIndex);
    const isApprover = this.isCurrentApprover(actualIndex);
    const isCompleted = this.isStepActionCompleted(actualIndex);
    const stepStatus = this.getStepDisplayStatus(stepIndex);

    // console.log('簽核操作區域顯示條件檢查:', {
    //   stepIndex,
    //   actualIndex,
    //   stepStatus,
    //   isReadOnly,
    //   isApprover,
    //   isCompleted,
    //   shouldShow: !isReadOnly && isApprover && !isCompleted,
    //   currentUserId: this.currentUserId,
    //   stepMode: this.stepIndex >= 0 ? 'single' : 'all'
    // });
  }

  /**
   * 簽核同意後發送通知給下一關簽核人員
   * 只在 approve() 方法中呼叫，reject() 方法不呼叫
   */
  private async sendNotificationToNextApprovers(currentStepIndex: number): Promise<void> {
    try {
      // 計算下一關的索引
      const actualCurrentIndex = this.getActualStepIndex(currentStepIndex);
      const nextStepIndex = actualCurrentIndex + 1;

      console.log('檢查是否需要發送簽核通知:', {
        currentStepIndex,
        actualCurrentIndex,
        nextStepIndex,
        totalSteps: this.approvalFlow?.length || 0
      });

      // 檢查是否有下一關
      if (!this.approvalFlow || nextStepIndex >= this.approvalFlow.length) {
        console.log('已是最後一關，發送完成通知給申請人');
        // 🆕 發送完成通知給申請人
        await this.sendCompletionNotificationToSubmitter();
        return;
      }

      const nextStep = this.approvalFlow[nextStepIndex];

      // 🔍 DEBUG: 次のステップの構造を確認
      console.log('🔍 [DEBUG] 次のステップの完全な構造:', JSON.stringify(nextStep, null, 2));
      console.log('🔍 [DEBUG] nextStep.stepName:', nextStep?.stepName);
      console.log('🔍 [DEBUG] nextStep.name:', (nextStep as any)?.name);
      console.log('🔍 [DEBUG] nextStep.level:', nextStep?.level);
      console.log('🔍 [DEBUG] nextStep.order:', (nextStep as any)?.order);

      // 檢查下一關是否有 approverList
      if (!nextStep || !(nextStep as any).approverList || (nextStep as any).approverList.length === 0) {
        console.log('下一關沒有 approverList 或為空，不發送通知', {
          hasNextStep: !!nextStep,
          hasApproverList: !!(nextStep as any).approverList,
          approverListLength: (nextStep as any).approverList?.length || 0
        });
        return;
      }

      // 提取下一關簽核人員的 ID
      const approverList = (nextStep as any).approverList;
      const approverIds = approverList.map((approver: any) => approver.id).filter((id: any) => id);

      if (approverIds.length === 0) {
        console.log('下一關簽核人員 ID 列表為空，不發送通知');
        return;
      }

      console.log('準備發送簽核通知:', {
        nextStepName: nextStep.stepName,
        approverCount: approverIds.length,
        approverIds
      });

      // 取得當前語系並確保是支援的語系
      const currentLocale = this.getCurrentLanguageSync();

      // 取得電子郵件地址とユーザー情報
      const emailResponse = await this.getUserEmails(approverIds);
      console.log('電子郵件取得結果:', emailResponse);

      // 📧 usersInfoから全ての情報を抽出（データ整合性を保証）
      if (emailResponse && emailResponse.success && emailResponse.usersInfo && Array.isArray(emailResponse.usersInfo) && emailResponse.usersInfo.length > 0) {

        // 📧 usersInfoからメールアドレスと氏名を同時に抽出
        const emailToNameMap = new Map<string, string>();
        const recipientEmails: string[] = [];

        emailResponse.usersInfo.forEach((user: any) => {
          if (user && user.email) {
            // メールアドレスを配列に追加
            recipientEmails.push(user.email);
            
            // 氏名マッピングを作成（優先順位: fullName > username > email local-part）
            const displayName = String(user.fullName || user.username || user.email.split('@')[0] || '').trim();
            emailToNameMap.set(user.email, displayName);
          }
        });

        console.log('📧 抽出されたメールアドレス:', recipientEmails);
        console.log('📧 氏名マッピング:', Array.from(emailToNameMap.entries()));

        if (recipientEmails.length === 0) {
          console.warn('⚠️ usersInfoからメールアドレスを抽出できませんでした');
          return;
        }

        // 🔧 取得表單名稱與申請日時（優先使用實際名稱）
        let actualFormName = '簽核表單';
        let actualSubmittedAt: string | undefined = undefined;
        let actualFlowName: string | undefined = undefined;
        const dataId = this.id || this.formId;  // 嘗試兩個 ID

        if (dataId) {
          try {
            const apiUrl = environment.apiUrl;
            console.log('🔍 查詢表單名稱，ID:', dataId);

            const formDataResponse = await this.http.get<any>(
              `${apiUrl}/api/form-manager/get-form-data`,
              {
                params: {
                  id: dataId
                }
              }
            ).toPromise();

            if (formDataResponse && formDataResponse.success && formDataResponse.data) {
              const formData = formDataResponse.data;

              // 🔍 DEBUG: フォームデータの構造を詳しく確認
              console.log('🔍 [DEBUG] 完全なフォームデータ:', formData);
              console.log('🔍 [DEBUG] formData.formName:', formData.formName);
              console.log('🔍 [DEBUG] formData.componentname:', formData.componentname);
              console.log('🔍 [DEBUG] formData.formname:', formData.formname);
              console.log('🔍 [DEBUG] formData.form_name:', formData.form_name);
              console.log('🔍 [DEBUG] formData.referencedata:', formData.referencedata);
              console.log('🔍 [DEBUG] 利用可能なキー:', Object.keys(formData));

              // 🔧 嘗試多種可能的欄位名稱（合併 master 和 JSAI 的邏輯）
              actualFormName = formData.formName ||
                              formData.componentname ||
                              formData.formname ||
                              formData.form_name ||
                              formData.referencedata?.['work-name']?.value ||
                              formData['work-name']?.value ||
                              formData.workName?.value ||
                              formData.project_name?.value ||
                              formData.projectName?.value ||
                              actualFormName;
              
              console.log('🔍 [DEBUG] 最終的に選択されたフォーム名:', actualFormName);
              
              // 🔧 フォーム定義から正しい表示名を取得
              if (formData.componentname) {
                try {
                  console.log('🔍 [DEBUG] フォーム定義を取得します。formid:', formData.componentname);
                  const formConfigResponse = await this.http.get<any>(
                    `${apiUrl}/api/form-manager/get_specific_form_config`,
                    {
                      params: {
                        formid: formData.componentname
                      }
                    }
                  ).toPromise();
                  
                  if (formConfigResponse && formConfigResponse.success && formConfigResponse.data) {
                    console.log('🔍 [DEBUG] フォーム定義レスポンス:', formConfigResponse.data);
                    console.log('🔍 [DEBUG] フォーム定義のキー:', Object.keys(formConfigResponse.data));
                    
                    // フォーム定義から表示名を取得
                    const formDisplayName = formConfigResponse.data.name || 
                                           formConfigResponse.data.formName ||
                                           formConfigResponse.data.displayName;
                    
                    if (formDisplayName) {
                      actualFormName = formDisplayName;
                      console.log('✅ [DEBUG] フォーム定義から表示名を取得:', actualFormName);
                    }
                  }
                } catch (error) {
                  console.error('❌ フォーム定義の取得に失敗:', error);
                }
              }
              
              // 📅 取得申請日時（優先使用 submittedAt，若無則使用 _ts）
              actualSubmittedAt = formDataResponse.data.submittedAt || 
                                 (formDataResponse.data._ts ? new Date(formDataResponse.data._ts * 1000).toISOString() : undefined);
              
              // 🔧 取得簽核流程名稱（如果存在）
              if (formDataResponse.data.approval_flow_1?.value) {
                const flowData = formDataResponse.data.approval_flow_1.value;
                actualFlowName = flowData.flowName || flowData.value?.flowName;
              }
              
              console.log('✅ 成功取得表單名稱:', actualFormName);
              console.log('📅 申請日時:', actualSubmittedAt);
              console.log('📋 簽核流程名稱:', actualFlowName);
            } else {
              console.warn('⚠️ API 回應失敗或沒有資料');
            }
          } catch (error) {
            console.error('❌ 查詢表單名稱失敗:', error);
          }
        } else {
          console.warn('⚠️ 無 this.id 也無 this.formId，無法查詢表單名稱');
        }

        // JSAI-509: 取得下一關簽核人員的帳號
        let approverAccount = '';
        if (approverList && approverList.length > 0) {
          approverAccount = approverList[0].account ||
                           approverList[0].username ||
                           approverList[0].email ||
                           '';
        }

        // 🔍 DEBUG: 印出當前步驟的完整結構
        console.log('🔍 [DEBUG] 當前步驟 (actualCurrentIndex=' + actualCurrentIndex + ') 的完整結構:',
          JSON.stringify(this.approvalFlow[actualCurrentIndex], null, 2));

        // 🔍 DEBUG: 印出前一步驟的完整結構（如果存在）
        if (actualCurrentIndex > 0) {
          console.log('🔍 [DEBUG] 前一步驟 (index=' + (actualCurrentIndex - 1) + ') 的完整結構:',
            JSON.stringify(this.approvalFlow[actualCurrentIndex - 1], null, 2));
        }

        // 🔍 DEBUG: 印出當前使用者資訊
        console.log('🔍 [DEBUG] 當前使用者資訊:', {
          currentUserId: this.currentUserId,
          currentUserName: this.currentUserName,
          currentUserInfo: this.currentUserInfo
        });

        // 🔍 DEBUG: 嘗試從其他來源取得使用者 ID
        const userIdFromService = this.userService.getCurrentUserId();
        const userInfoFromAuth = this.authService.getUserInfo();
        console.log('🔍 [DEBUG] 其他來源的使用者資訊:', {
          userIdFromService,
          userIdFromAuth: userInfoFromAuth.userid,
          userNameFromAuth: userInfoFromAuth.username
        });

        // 251026: 取得前一關簽核者的帳號+姓名
        // 🔧 修正：應該從剛剛完成的步驟 (actualCurrentIndex) 中取得簽核人資訊
        let previousApproverInfo = '';
        const currentStep: any = this.approvalFlow[actualCurrentIndex];

        // 先確保有可用的使用者 ID 和名稱 - 使用 AuthService 取得
        const effectiveUserId = this.currentUserId || userIdFromService || userInfoFromAuth.userid || '';
        const effectiveUserName = this.currentUserName || userInfoFromAuth.username || '';

        console.log('🔍 [DEBUG] 使用的有效使用者資訊:', {
          effectiveUserId,
          effectiveUserName
        });

        // 嘗試從 approver 物件取得（優先使用名稱）
        if (currentStep?.approver) {
          const approverUserName = currentStep.approver.userName || currentStep.approver.name || '';
          // 只顯示名稱，不顯示 ID
          previousApproverInfo = approverUserName || effectiveUserName;
          console.log('✅ 從 approver 物件取得前一關簽核者:', previousApproverInfo);
        }

        // 如果 approver 沒有資料，嘗試從 approverList 取得
        if (!previousApproverInfo && currentStep?.approverList && currentStep.approverList.length > 0) {
          // 使用有效的使用者 ID 匹配
          const matchedApprover = currentStep.approverList.find((a: any) => a.id === effectiveUserId);
          if (matchedApprover) {
            // 只顯示名稱
            previousApproverInfo = matchedApprover.name || effectiveUserName;
            console.log('✅ 從 approverList 匹配當前使用者取得前一關簽核者:', previousApproverInfo);
          } else {
            // 如果找不到匹配，使用當前登入使用者名稱
            previousApproverInfo = effectiveUserName;
            console.log('⚠️ 無法匹配 approverList，使用當前登入使用者名稱:', previousApproverInfo);
          }
        }

        // 最後的備用方案：只使用名稱
        if (!previousApproverInfo) {
          previousApproverInfo = effectiveUserName || '未知使用者';
          console.log('⚠️ 使用備用方案：當前登入使用者名稱:', previousApproverInfo);
        }

        console.log('📧 最終前一關簽核者資訊:', previousApproverInfo);

        // 🔧 次のステップ名を取得（複数の候補を試行）
        const nextStepName = nextStep?.stepName || 
                            (nextStep as any)?.name || 
                            `第${nextStepIndex + 1}關` ||
                            undefined;
        
        // 🔧 次のステップレベルを取得（複数の候補を試行）
        const nextStepLevel = nextStep?.level || 
                             (nextStep as any)?.order || 
                             nextStepIndex + 1 ||
                             undefined;
        
        console.log('🔍 [DEBUG] 最終的に使用するステップ情報:', {
          nextStepName,
          nextStepLevel,
          nextStepIndex: nextStepIndex + 1
        });

        // 準備簽核資訊
        const approvalInfo = {
          formName: actualFormName,  // 🔧 修正：使用實際の表單名稱
          flowName: actualFlowName,  // 🔧 修正：APIから取得した実際のflowName（undefinedの可能性あり）
          currentStepName: this.approvalFlow[actualCurrentIndex]?.stepName || 
                          (this.approvalFlow[actualCurrentIndex] as any)?.name || 
                          '',
          currentStepLevel: actualCurrentIndex + 1,
          nextStepName: nextStepName,
          nextStepLevel: nextStepLevel,
          previousApproverName: previousApproverInfo,
          previousApprovalResult: '同意', // 固定為同意,因為只有在 approve 時才會呼叫
          taskUrl: this.generateTaskUrl(),
          deadline: undefined, // 可以根據需要添加期限邏輯
          companyId: environment.companyid,
          submittedAt: actualSubmittedAt, // 📅 申請日時
          approverAccount: approverAccount, // JSAI-509: 加入簽核人員帳號
          formId: this.formId // JSAI-509: 加入表單ID
        };

        console.log('準備發送郵件,簽核資訊:', approvalInfo);

        // 發送通知郵件
        const emailResult = await this.sendApprovalNotification(
          recipientEmails,  // 📧 usersInfoから抽出したメールアドレス配列
          approvalInfo, 
          currentLocale,
          emailToNameMap  // 📧 氏名マッピングを渡す
        );

        console.log('簽核通知郵件發送成功', {
          nextStepIndex,
          nextStepName: nextStep.stepName,
          recipientCount: recipientEmails.length,
          locale: currentLocale,
          result: emailResult
        });
      } else {
        console.warn('無法取得下一關簽核人員的電子郵件地址', emailResponse);
      }
    } catch (error) {
      console.error('發送簽核通知失敗:', error);
      // 注意：不要讓郵件發送失敗影響簽核流程
    }
  }

  /**
   * 發送拒絕/退回通知給受影響的關卡人員
   */
  private async sendRejectNotification(currentStepIndex: number): Promise<void> {
    try {
      const actualCurrentIndex = this.getActualStepIndex(currentStepIndex);

      console.log('開始處理拒絕/退回通知:', {
        currentStepIndex,
        actualCurrentIndex,
        rejectType: this.rejectFormData.rejectType,
        returnToStepId: this.rejectFormData.returnToStepId
      });

      // 根據拒絕類型決定通知範圍
      let affectedStepIndices: number[] = [];

      switch (this.rejectFormData.rejectType) {
        case 'complete_reject':
        case 'return_to_submitter':
          // 通知從提交人到當前關卡的前一關的所有人
          // 範圍：0 到 actualCurrentIndex-1（不包含當前關卡）
          affectedStepIndices = Array.from({length: actualCurrentIndex}, (_, i) => i);
          break;

        case 'return_to_step':
          // 通知從退回目標關卡到當前關卡的前一關
          const returnToStep = this.availableReturnSteps.find(step =>
            step.stepId === this.rejectFormData.returnToStepId
          );
          if (returnToStep) {
            const targetStepIndex = returnToStep.actualIndex;
            // 範圍：targetStepIndex 到 actualCurrentIndex-1
            affectedStepIndices = Array.from(
              {length: actualCurrentIndex - targetStepIndex},
              (_, i) => targetStepIndex + i
            );
          }
          break;
      }

      console.log('受影響的關卡索引:', affectedStepIndices);

      if (affectedStepIndices.length === 0) {
        console.log('沒有需要通知的關卡');
        return;
      }

      // 收集所有受影響關卡的人員 ID
      const allAffectedUserIds: string[] = [];

      // 如果影響到提交人（索引 0 在範圍內），需要特別處理
      if (affectedStepIndices.includes(0)) {
        // 添加提交人 - 從 approvalInstance 中取得
        const submitterId = (this.approvalInstance as any)?.submitterId || (this.approvalInstance as any)?.createdBy;
        if (submitterId) {
          allAffectedUserIds.push(submitterId);
        }
      }

      // ⭐ 分組收集：需要重新簽核的人 vs 僅需知道的人
      const targetStepIndex = this.rejectFormData.rejectType === 'return_to_step' ?
        this.availableReturnSteps.find(step => step.stepId === this.rejectFormData.returnToStepId)?.actualIndex :
        undefined;

      const approversNeedAction: string[] = [];  // 需要重新簽核的人
      const approversForInfo: string[] = [];     // 僅需知道的人
      const submitterNeedAction: string[] = [];  // 提交人需要重新填寫

      // 🔍 特殊處理：退回給提交人的情況
      if (this.rejectFormData.rejectType === 'return_to_submitter') {
        // 嘗試從多個來源取得提交人 ID
        let submitterId = (this.approvalInstance as any)?.submitterId ||
                         (this.approvalInstance as any)?.createdBy ||
                         (this.approvalInstance as any)?.initiator?.userId;

        console.log('🔍 Debug - 提交人 ID 來源 (第一階段):', {
          submitterId: (this.approvalInstance as any)?.submitterId,
          createdBy: (this.approvalInstance as any)?.createdBy,
          initiatorUserId: (this.approvalInstance as any)?.initiator?.userId,
          firstAttemptResult: submitterId
        });

        // 如果從 approvalInstance 取不到，嘗試從表單資料查詢
        if (!submitterId && this.id) {
          console.log('📋 嘗試從表單資料查詢提交人 ID，表單資料 ID:', this.id);
          try {
            // 直接使用 HTTP 請求查詢表單資料
            const apiUrl = environment.apiUrl;
            const formDataResponse = await this.http.get<any>(
              `${apiUrl}/api/form-manager/get-form-data`,
              {
                params: {
                  companyid: environment.companyid,
                  id: this.id  // 使用表單資料的實際 ID，而不是 formId
                }
              }
            ).toPromise();

            if (formDataResponse && formDataResponse.success && formDataResponse.data) {
              submitterId = formDataResponse.data.created_by;
              console.log('✅ 從表單資料取得提交人 ID:', submitterId);
            }
          } catch (error) {
            console.error('❌ 查詢表單資料失敗:', error);
          }
        }

        console.log('🔍 Debug - 最終提交人 ID:', submitterId);

        if (submitterId) {
          submitterNeedAction.push(submitterId);
          console.log('📝 提交人需要重新填寫:', submitterId);
        } else {
          console.warn('⚠️ 無法找到提交人 ID，無法發送退回通知給提交人');
        }
      }

      // 收集各關卡的簽核人員
      affectedStepIndices.forEach(stepIndex => {
        if (stepIndex >= 0 && stepIndex < (this.approvalFlow?.length || 0)) {
          const step = this.approvalFlow?.[stepIndex];

          console.log(`檢查關卡 ${stepIndex}:`, {
            stepName: step?.stepName,
            hasApproverList: !!(step as any)?.approverList,
            approverCount: (step as any)?.approverList?.length || 0,
            isTargetStep: stepIndex === targetStepIndex,
            rejectType: this.rejectFormData.rejectType
          });

          if (step && (step as any).approverList) {
            const approverIds = (step as any).approverList
              .map((approver: any) => approver.id)
              .filter((id: string) => id && !allAffectedUserIds.includes(id));

            // 判斷是需要重新簽核還是僅需知道
            if (this.rejectFormData.rejectType === 'return_to_step' && stepIndex === targetStepIndex) {
              // 退回到指定階段：目標階段的人需要重新簽核
              console.log(`⭐ 關卡 ${stepIndex} (${step.stepName}) 需要重新簽核:`, approverIds);
              approversNeedAction.push(...approverIds);
            } else {
              // 其他情況：僅需知道
              console.log(`📋 關卡 ${stepIndex} (${step.stepName}) 僅需知道:`, approverIds);
              approversForInfo.push(...approverIds);
            }

            allAffectedUserIds.push(...approverIds);
          }
        }
      });

      console.log('需要重新簽核的人員 ID:', approversNeedAction);
      console.log('僅需知道的人員 ID:', approversForInfo);
      console.log('所有受影響的人員 ID:', allAffectedUserIds);

      if (allAffectedUserIds.length === 0) {
        console.log('沒有找到需要通知的人員');
        return;
      }

      // 取得人員 email 地址
      const emailResponse = await this.getUserEmails(allAffectedUserIds);
      console.log('拒絕通知 - 電子郵件取得結果:', emailResponse);

      if (emailResponse && emailResponse.success && emailResponse.emails && emailResponse.emails.length > 0) {
        const currentLocale = this.i18nService.currentLangValue;

        // ⭐ 分別發送兩種類型的郵件
        const results: any[] = [];

        // 🔧 取得兩組人員的 email（需要分別查詢 API）
        let actionEmails: string[] = [];
        let infoEmails: string[] = [];

        // 1. 發送給需要重新簽核的人（優先級高）
        if (approversNeedAction.length > 0) {
          console.log('🔍 Debug - 需要重新簽核的人員 ID:', approversNeedAction);
          const actionEmailResponse = await this.getUserEmails(approversNeedAction);
          
          // 📧 usersInfoからメールアドレスと氏名を同時に抽出
          const actionEmailToNameMap = new Map<string, string>();
          if (actionEmailResponse && actionEmailResponse.success && actionEmailResponse.usersInfo && Array.isArray(actionEmailResponse.usersInfo)) {
            actionEmailResponse.usersInfo.forEach((user: any) => {
              if (user && user.email) {
                actionEmails.push(user.email);
                const displayName = String(user.fullName || user.username || user.email.split('@')[0] || '').trim();
                actionEmailToNameMap.set(user.email, displayName);
              }
            });
          }
          
          console.log('🔍 Debug - actionEmails:', actionEmails);
          console.log('🔍 Debug - actionEmailToNameMap:', Array.from(actionEmailToNameMap.entries()));

          if (actionEmails.length > 0) {
            const actionRejectInfo = {
              formName: (this.approvalInstance as any)?.formName || '表單',
              rejectType: this.rejectFormData.rejectType,
              rejectReason: this.rejectFormData.rejectReason,
              rejectedBy: this.currentUserName || this.currentUserId,
              rejectedAt: new Date().toISOString(),
              returnToStepName: this.rejectFormData.rejectType === 'return_to_step' ?
                this.availableReturnSteps.find(step => step.stepId === this.rejectFormData.returnToStepId)?.stepName : null,
              requiresAction: true  // ⭐ 標記需要重新簽核
            };

            const actionResult = await this.sendRejectNotificationEmail(
              actionEmails,
              actionRejectInfo,
              currentLocale as 'zh-TW' | 'ja-JP' | 'en-US',
              actionEmailToNameMap  // 📧 氏名マッピングを渡す
            );
            results.push({ type: 'action', result: actionResult });
            console.log('✅ 已發送「需要重新簽核」郵件:', actionEmails.length, '封');
          }
        }

        // 2. 發送給提交人（需要重新填寫）
        if (submitterNeedAction.length > 0) {
          console.log('🔍 Debug - 提交人需要重新填寫 ID:', submitterNeedAction);
          const submitterEmailResponse = await this.getUserEmails(submitterNeedAction);
          let submitterEmails: string[] = [];
          
          // 📧 usersInfoからメールアドレスと氏名を同時に抽出
          const submitterEmailToNameMap = new Map<string, string>();
          if (submitterEmailResponse && submitterEmailResponse.success && submitterEmailResponse.usersInfo && Array.isArray(submitterEmailResponse.usersInfo)) {
            submitterEmailResponse.usersInfo.forEach((user: any) => {
              if (user && user.email) {
                submitterEmails.push(user.email);
                const displayName = String(user.fullName || user.username || user.email.split('@')[0] || '').trim();
                submitterEmailToNameMap.set(user.email, displayName);
              }
            });
          }
          
          console.log('🔍 Debug - submitterEmails:', submitterEmails);
          console.log('🔍 Debug - submitterEmailToNameMap:', Array.from(submitterEmailToNameMap.entries()));

          if (submitterEmails.length > 0) {
            const submitterRejectInfo = {
              formName: (this.approvalInstance as any)?.formName || '表單',
              rejectType: this.rejectFormData.rejectType,
              rejectReason: this.rejectFormData.rejectReason,
              rejectedBy: this.currentUserName || this.currentUserId,
              rejectedAt: new Date().toISOString(),
              returnToStepName: null,
              requiresAction: true  // ⭐ 提交人需要重新填寫
            };

            const submitterResult = await this.sendRejectNotificationEmail(
              submitterEmails,
              submitterRejectInfo,
              currentLocale as 'zh-TW' | 'ja-JP' | 'en-US',
              submitterEmailToNameMap  // 📧 氏名マッピングを渡す
            );
            results.push({ type: 'submitter', result: submitterResult });
            console.log('📝 已發送「需要重新填寫」郵件給提交人:', submitterEmails.length, '封');
          }
        }

        // 3. 發送給僅需知道的人（一般通知）
        if (approversForInfo.length > 0) {
          console.log('🔍 Debug - 僅需知道的人員 ID:', approversForInfo);
          const infoEmailResponse = await this.getUserEmails(approversForInfo);
          
          // 📧 usersInfoからメールアドレスと氏名を同時に抽出
          const infoEmailToNameMap = new Map<string, string>();
          if (infoEmailResponse && infoEmailResponse.success && infoEmailResponse.usersInfo && Array.isArray(infoEmailResponse.usersInfo)) {
            infoEmailResponse.usersInfo.forEach((user: any) => {
              if (user && user.email) {
                infoEmails.push(user.email);
                const displayName = String(user.fullName || user.username || user.email.split('@')[0] || '').trim();
                infoEmailToNameMap.set(user.email, displayName);
              }
            });
          }
          
          console.log('🔍 Debug - infoEmails:', infoEmails);
          console.log('🔍 Debug - infoEmailToNameMap:', Array.from(infoEmailToNameMap.entries()));

          if (infoEmails.length > 0) {
            const infoRejectInfo = {
              formName: (this.approvalInstance as any)?.formName || '表單',
              rejectType: this.rejectFormData.rejectType,
              rejectReason: this.rejectFormData.rejectReason,
              rejectedBy: this.currentUserName || this.currentUserId,
              rejectedAt: new Date().toISOString(),
              returnToStepName: this.rejectFormData.rejectType === 'return_to_step' ?
                this.availableReturnSteps.find(step => step.stepId === this.rejectFormData.returnToStepId)?.stepName : null,
              requiresAction: false  // ⭐ 標記僅需知道
            };

            const infoResult = await this.sendRejectNotificationEmail(
              infoEmails,
              infoRejectInfo,
              currentLocale as 'zh-TW' | 'ja-JP' | 'en-US',
              infoEmailToNameMap  // 📧 氏名マッピングを渡す
            );
            results.push({ type: 'info', result: infoResult });
            console.log('📋 已發送「流程退回通知」郵件:', infoEmails.length, '封');
          }
        }

        console.log('拒絕通知郵件發送結果:', {
          rejectType: this.rejectFormData.rejectType,
          affectedStepCount: affectedStepIndices.length,
          actionEmailCount: actionEmails.length,
          infoEmailCount: infoEmails.length,
          totalRecipients: actionEmails.length + infoEmails.length,
          results: results
        });
      } else {
        console.warn('無法取得受影響人員的電子郵件地址', emailResponse);
      }

    } catch (error) {
      console.error('發送拒絕通知失敗:', error);
      // 注意：不要讓郵件發送失敗影響簽核流程
    }
  }

  /**
   * 發送拒絕通知郵件
   */
  private async sendRejectNotificationEmail(
    recipientEmails: string[],
    rejectInfo: any,
    locale: 'zh-TW' | 'ja-JP' | 'en-US',
    recipientNames?: Map<string, string>  // 📧 氏名マッピングを追加
  ): Promise<any> {
    try {
      const result = await this.emailService.sendRejectNotification(
        recipientEmails,
        rejectInfo,
        locale,
        recipientNames  // 📧 氏名マッピングを渡す
      ).toPromise();
      console.log('拒絕通知郵件發送結果:', result);
      return result;
    } catch (error) {
      console.error('拒絕通知郵件發送失敗:', error);
      throw error;
    }
  }

  /**
   * 模擬簽核操作（用於 Mock 資料狀態）
   */
  simulateApprovalAction(action: 'approve' | 'reject', stepIndex: number) {
    if (!this.approvalInstance) return;

    const currentStep = this.approvalInstance.steps[stepIndex] as ApprovalStepInstanceWithAction;

    // 模擬處理延遲
    setTimeout(() => {
      // 更新當前步驟狀態
      currentStep.status = action === 'approve' ? 'approved' : 'rejected';
      currentStep.comment = this.comment;
      currentStep.signature = this.signature?.imageData || null;
      currentStep.actionDate = new Date();

      // 保存自訂表單資料
      if (this.customFormData[stepIndex]) {
        (currentStep as any).customFormData = { ...this.customFormData[stepIndex] };
      }

      // 設置操作結果
      const actionText = action === 'approve' ? this.i18nService.translate('approval.status.approve') : this.i18nService.translate('approval.status.reject');
      currentStep.actionResult = {
        success: true,
        message: this.i18nService.translate('approval.status.action_completed', { action: actionText })
      };

      // 如果同意且還有下一步驟，進入下一步驟
      if (action === 'approve' && stepIndex < this.approvalInstance!.steps.length - 1) {
        this.approvalInstance!.currentStepIndex = stepIndex + 1;
        this.approvalInstance!.steps[stepIndex + 1].status = 'in_progress';
      } else {
        // 如果是最後一步或被拒絕，結束流程
        this.approvalInstance!.status = action === 'approve' ? 'approved' : 'rejected';
        this.approvalInstance!.completedAt = new Date();
      }

      this.approvalInstance!.updatedAt = new Date();

      // 清空表單數據
      this.comment = '';
      this.signature = null;
      this.actionInProgress = false;

      // 通知父組件
      this.approvalComplete.emit(this.approvalInstance!);

      // 觸發變更檢測
      this.cdr.markForCheck();
    }, 500); // 500ms 延遲模擬網路請求
  }

  // 新增方法：檢查步驟是否已完成操作
  isStepActionCompleted(stepIndex: number): boolean {
    if (!this.approvalInstance) {
      return false;
    }

    // 單一階段模式時，檢查該階段的狀態
    if (this.stepIndex >= 0) {
      const stepStatus = this.getStepDisplayStatus(0); // 在單一階段模式中，displayIndex 總是 0
      return stepStatus === 'approved' || stepStatus === 'rejected';
    }

    // 一般模式時，檢查 actionResult
    const step = this.approvalInstance.steps[stepIndex] as ApprovalStepInstanceWithAction;
    const isCompleted = step.actionResult ? step.actionResult.success : false;

    return isCompleted;
  }

  /**
   * 取得要顯示的步驟列表（支援單一階段顯示）
   */
  getDisplaySteps(): ApprovalStepInstance[] {
    if (!this.approvalInstance) return [];

    // 如果指定了特定stepIndex，只顯示該階段
    if (this.stepIndex >= 0 && this.stepIndex < this.approvalInstance.steps.length) {
      return [this.approvalInstance.steps[this.stepIndex]];
    }

    // 否則顯示所有階段
    return this.approvalInstance.steps;
  }

  /**
   * 取得步驟的實際索引（用於單一階段顯示時的索引轉換）
   */
  getActualStepIndex(displayIndex: number): number {
    return this.stepIndex >= 0 ? this.stepIndex : displayIndex;
  }

  /**
   * 檢查階段是否為只讀模式（已完成或非當前階段）
   */
  isStepReadOnly(stepIndex: number): boolean {
    if (!this.approvalInstance) return true;

    const actualIndex = this.getActualStepIndex(stepIndex);

    // 單一階段模式時，檢查該階段的狀態
    if (this.stepIndex >= 0) {
      const stepStatus = this.getStepDisplayStatus(stepIndex);
      return stepStatus === 'approved' || stepStatus === 'rejected';
    }

    const step = this.approvalInstance.steps[actualIndex];

    // 如果有簽核結果資料，優先使用
    if (this.approvalResult && this.approvalResult.stepResults) {
      const stepResult = this.getStepResultFromApprovalResult(actualIndex);
      return stepResult?.completed === true;
    }

    // 已完成的階段（approved或rejected）為只讀
    return step.status === 'approved' || step.status === 'rejected';
  }

  /**
   * 從approvalResult中取得指定步驟的結果
   */
  getStepResultFromApprovalResult(stepIndex: number): any {
    return this.approvalResult?.stepResults?.find(
      (result: any) => result.stepIndex === stepIndex
    );
  }

  /**
   * 取得階段的歷史資料（意見、簽名、自訂表單資料等）
   */
  getStepHistoryData(stepIndex: number): any {
    if (!this.approvalInstance) return null;

    const actualIndex = this.getActualStepIndex(stepIndex);

    // 當顯示單一階段時，優先從 approvalFlow 中取得狀態資料
    if (this.stepIndex >= 0 && this.approvalFlow && this.approvalFlow[actualIndex]) {
      const flowStep = this.approvalFlow[actualIndex] as any;
      if (flowStep.status && (flowStep.status === 'approved' || flowStep.status === 'rejected')) {
        return {
          comment: flowStep.comment || '',
          signature: flowStep.signature || null,
          customFormData: this.extractCustomFormDataFromFlow(flowStep),
          actionDate: flowStep.actionDate || null,
          status: flowStep.status
        };
      }
    }

    // 優先從approvalResult中取得歷史資料
    if (this.approvalResult && this.approvalResult.stepResults) {
      const stepResult = this.getStepResultFromApprovalResult(actualIndex);
      if (stepResult && stepResult.completed) {
        return {
          comment: stepResult.comment || '',
          signature: stepResult.signature || null,
          customFormData: stepResult.customFormData || {},
          actionDate: stepResult.actionDate || null,
          status: stepResult.action === 'approve' ? 'approved' : 'rejected'
        };
      }
    }

    // 如果沒有approvalResult，從step資料中取得
    const step = this.approvalInstance.steps[actualIndex];
    return {
      comment: step.comment || '',
      signature: step.signature || null,
      customFormData: (step as any).customFormData || {},
      actionDate: step.actionDate || null,
      status: step.status
    };
  }

  /**
   * 取得階段的顯示狀態（考慮歷史資料）
   */
  getStepDisplayStatus(stepIndex: number): string {
    const actualIndex = this.getActualStepIndex(stepIndex);

    // 當顯示單一階段時，優先使用 approvalFlow 中的狀態
    if (this.stepIndex >= 0 && this.approvalFlow && this.approvalFlow[actualIndex]) {
      const flowStep = this.approvalFlow[actualIndex] as any;
      if (flowStep.status) {
        return flowStep.status;
      }
    }

    const historyData = this.getStepHistoryData(stepIndex);
    if (historyData && historyData.status) {
      return historyData.status;
    }

    return this.approvalInstance?.steps[actualIndex]?.status || 'pending';
  }
  getStepActionResult(stepIndex: number): { success: boolean, message: string } | null {
    if (!this.approvalInstance || !this.approvalInstance.steps[stepIndex]) return null;

    const step = this.approvalInstance.steps[stepIndex] as ApprovalStepInstanceWithAction;
    return step.actionResult || null;
  }

  /**
   * 從簽核流程資料建立簽核實例
   */
  createApprovalInstanceFromFlow() {
    if (!this.approvalFlow || this.approvalFlow.length === 0) return;

    console.log('🔍 [createApprovalInstanceFromFlow] approvalFlow 資料:', JSON.stringify(this.approvalFlow, null, 2));
    console.log(`🔍 [createApprovalInstanceFromFlow] 總步驟數: ${this.approvalFlow.length}`);

    const steps: ApprovalStepInstance[] = this.approvalFlow.map((flowStep: any, index) => {
      // 優先使用 approvalFlow 中的狀態資料
      let stepStatus: 'pending' | 'in_progress' | 'approved' | 'rejected' = 'pending';
      let stepComment = '';
      let stepSignature: string | null = null;
      let stepActionDate: Date | null = null;
      let stepCustomFormData: any = {};

      // 首先檢查 approvalFlow 中是否有狀態資料
      if (flowStep.status) {
        stepStatus = flowStep.status;
        stepComment = flowStep.comment || '';
        stepSignature = flowStep.signature || null;
        stepActionDate = flowStep.actionDate ? new Date(flowStep.actionDate) : null;

        // 從 customFormItems 中提取表單資料
        if (flowStep.customFormItems) {
          stepCustomFormData = {};
          flowStep.customFormItems.forEach((item: any) => {
            if (item.value !== undefined) {
              stepCustomFormData[item.name] = item.value;
            }
          });
        }
      } else if (this.approvalResult && this.approvalResult.stepResults) {
        // 如果 approvalFlow 沒有狀態，才檢查 approvalResult
        const stepResult = this.getStepResultFromApprovalResult(index);
        if (stepResult && stepResult.completed) {
          stepStatus = stepResult.action === 'approve' ? 'approved' : 'rejected';
          stepComment = stepResult.comment || '';
          stepSignature = stepResult.signature || null;
          stepActionDate = stepResult.actionDate ? new Date(stepResult.actionDate) : null;
          stepCustomFormData = stepResult.customFormData || {};
        } else if (index === (this.approvalResult.currentStep || 0)) {
          stepStatus = 'in_progress';
        }
      } else {
        // 沒有任何狀態資料時，使用預設邏輯
        stepStatus = index === 0 ? 'in_progress' : 'pending';
      }

      return {
        stepDefinitionId: flowStep.stepDefinitionId,
        name: flowStep.stepName,
        order: flowStep.level,
        approver: {
          userId: flowStep.userId || '', // 可能是空的，因為可能是角色基礎的簽核
          userName: flowStep.userName || this.getStepDepartmentRoleInfo(index),
          roleId: flowStep.roleId || '',
          roleName: flowStep.roleName || '',
          departmentId: flowStep.departmentId || '',
          departmentName: flowStep.departmentName || ''
        },
        status: stepStatus,
        comment: stepComment,
        signature: stepSignature,
        actionDate: stepActionDate,
        customFormData: stepCustomFormData
      } as ApprovalStepInstance & { customFormData: any };
    });

    const currentStepIndex = this.approvalResult?.currentStep || 0;
    let overallStatus: 'in_progress' | 'approved' | 'rejected' = 'in_progress';

    // 判斷整體狀態
    if (this.approvalResult) {
      const lastStepResult = this.approvalResult.stepResults?.find(
        (result: any) => result.stepIndex === steps.length - 1
      );
      if (lastStepResult && lastStepResult.completed) {
        overallStatus = lastStepResult.action === 'approve' ? 'approved' : 'rejected';
      } else if (this.approvalResult.stepResults?.some((result: any) =>
        result.completed && result.action === 'reject'
      )) {
        overallStatus = 'rejected';
      }
    }

    this.approvalInstance = {
      id: `approval_${this.formId}_${Date.now()}`,
      flowDefinitionId: 'custom_flow',
      formId: this.formId,
      formTypeId: 'custom_form',
      initiator: {
        userId: this.currentUserId,
        userName: this.currentUserName || 'Current User',
        departmentId: '',
        departmentName: ''
      },
      status: overallStatus,
      currentStepIndex: currentStepIndex,
      steps: steps,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // 觸發變更檢測
    this.cdr.markForCheck();

    // ❌ 移除此處的第一關通知發送邏輯
    // 原因：第一關通知應該由 dynamic-form.component.ts 的 checkAndSendFirstStageNotification 方法統一處理
    // 避免重複發送通知

    // 🔔 JSAI-509 & JSAI-799: 第一關簽核通知已改由 dynamic-form 元件統一處理
    // 此元件只負責後續簽核關卡的通知（approve 時的 sendNotificationToNextApprovers）
  }

  /**
   * 🆕 發送簽核流程初始化通知給第一關簽核人
   * JSAI-509 & JSAI-799: 當施工申請單完成編輯並提交時，應立即通知第一關簽核人
   */
  private async sendInitialApprovalNotification(): Promise<void> {
    try {
      console.log('🔔 開始發送第一關簽核通知');

      // 檢查第一關是否有 approverList
      const firstStep = this.approvalFlow[0];
      if (!firstStep || !(firstStep as any).approverList || (firstStep as any).approverList.length === 0) {
        console.warn('⚠️ 第一關沒有 approverList 或為空，無法發送通知');
        return;
      }

      // 取得第一關簽核人員的 ID
      const approverList = (firstStep as any).approverList;
      const approverIds = approverList.map((approver: any) => approver.id).filter((id: any) => id);

      if (approverIds.length === 0) {
        console.warn('⚠️ 第一關簽核人員 ID 列表為空，無法發送通知');
        return;
      }

      // 取得第一關簽核人員的 email
      const emailResponse = await this.getUserEmails(approverIds);

      if (!emailResponse || !emailResponse.success || !emailResponse.emails || emailResponse.emails.length === 0) {
        console.warn('⚠️ 無法取得第一關簽核人員的電子郵件', emailResponse);
        return;
      }

      // 取得當前語系，並轉換為支援的語系格式
      const currentLocale = this.i18nService.currentLangValue as 'zh-TW' | 'en-US' | 'ja-JP';

      // 取得工程名稱
      let projectName = this.i18nService.translate('approval_status.default.unnamed_project');
      try {
        console.log('🔍 開始取得工程名稱, this.id:', this.id, 'this.formId:', this.formId);
        
        const url = `${environment.apiUrl}/api/form-manager/get-form-data`;
        const params = {
          companyid: environment.companyid,
          id: this.id  // 使用 recordId 而非 formId
        };

        console.log('🔍 API 請求參數:', params);

        const response = await firstValueFrom(this.http.get<any>(url, { params }));

        console.log('🔍 API 回應:', response);

        if (response.success && response.data) {
          const formData = response.data;
          
          console.log('🔍 formData.referencedata:', formData.referencedata);

          if (formData.referencedata?.['work-name']?.value) {
            projectName = formData.referencedata['work-name'].value;
            console.log('✅ 成功取得工程名稱:', projectName);
          } else {
            console.warn('⚠️ referencedata["work-name"] 不存在或無值');
            console.log('🔍 referencedata 的所有 key:', Object.keys(formData.referencedata || {}));
          }
        } else {
          console.warn('⚠️ API 回應格式不正確:', response);
        }
      } catch (error) {
        console.error('❌ 取得表單資料失敗,使用預設工程名稱:', error);
      }

      // 取得第一關簽核人員帳號
      let approverAccount = '';
      if (approverList && approverList.length > 0) {
        approverAccount = approverList[0].account ||
                         approverList[0].username ||
                         approverList[0].email ||
                         '';
      }

      // 準備簽核資訊
      const approvalInfo = {
        formName: '施工申請單',
        flowName: '簽核流程',
        currentStepName: '', // 第一關沒有前一關
        currentStepLevel: 0,
        nextStepName: this.approvalFlow[0]?.stepName || '第一關',
        nextStepLevel: 1,
        previousApproverName: '提交人',
        previousApprovalResult: '提交',
        taskUrl: this.generateTaskUrl(),
        deadline: undefined,
        companyId: environment.companyid,
        projectName: projectName,
        approverAccount: approverAccount,
        formId: this.formId
      };

      console.log('🔔 發送第一關簽核通知，資訊:', approvalInfo);

      // 發送通知郵件
      const emailResult = await this.sendApprovalNotification(
        emailResponse.emails,
        approvalInfo,
        currentLocale
      );

      console.log('✅ 第一關簽核通知發送成功', {
        recipientCount: emailResponse.emails.length,
        locale: currentLocale,
        result: emailResult
      });
    } catch (error) {
      console.error('❌ 發送第一關簽核通知失敗:', error);
      // 不要讓郵件發送失敗影響簽核流程
    }
  }

  get statusText(): string {
    if (!this.approvalInstance) return this.i18nService.translate('approval.status.not_started');

    switch (this.approvalInstance.status) {
      case 'in_progress': return this.i18nService.translate('approval.status.in_progress');
      case 'approved': return this.i18nService.translate('approval.status.approved');
      case 'rejected': return this.i18nService.translate('approval.status.rejected');
      case 'returned': return this.i18nService.translate('approval.status.returned');
      default: return this.i18nService.translate('approval.status.unknown');
    }
  }

  get statusClass(): string {
    if (!this.approvalInstance) return 'status-pending';

    switch (this.approvalInstance.status) {
      case 'in_progress': return 'status-in-progress';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'returned': return 'status-returned';
      default: return 'status-pending';
    }
  }

  getStepClass(status: string): string {
    return `step-${status}`;
  }

  getStatusClass(status: string): string {
    return `status-${status}`;
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'pending': return this.i18nService.translate('approval.status.step.pending');
      case 'in_progress': return this.i18nService.translate('approval.status.step.in_progress');
      case 'approved': return this.i18nService.translate('approval.status.step.approved');
      case 'rejected': return this.i18nService.translate('approval.status.step.rejected');
      default: return this.i18nService.translate('approval.status.step.unknown');
    }
  }

  /**
   * 取得指定步驟的可簽核人員列表
   */
  getStepApproverList(stepIndex: number): any[] {
    if (!this.approvalFlow || !this.approvalFlow[stepIndex]) {
      return [];
    }

    const step = this.approvalFlow[stepIndex];
    return (step as any).approverList || [];
  }

  /**
   * 檢查目前使用者是否在可簽核人員列表中
   */
  isCurrentUserInApproverList(stepIndex: number): boolean {
    const approverList = this.getStepApproverList(stepIndex);
    return approverList.some(approver => approver.id === this.currentUserId);
  }

  /**
   * ✨ v2.0.0 取得步驟的部門角色資訊（支援多簽核者）
   */
  getStepDepartmentRoleInfo(stepIndex: number): string {
    if (!this.approvalFlow || !this.approvalFlow[stepIndex]) {
      return '';
    }

    const step = this.approvalFlow[stepIndex] as any;

    // ✨ 新格式：支援多簽核者
    if (step.approvers && step.approvers.length > 0) {
      const approverTexts = step.approvers.map((approver: any) => {
        if (approver.type === 'role_based') {
          const deptName = approver.departmentName || '未知部門';
          const roleName = approver.roleName || '未知角色';
          return `${deptName} - ${roleName}`;
        } else if (approver.type === 'specific_user') {
          return approver.userName || this.i18nService.translate('form.approval_flow.specific_user');
        }
        return '';
      }).filter((text: string) => text !== '');

      return approverTexts.join('；');
    }

    // 向下相容：舊格式（單簽核者）
    if (step.approverType === 'role_based') {
      const deptName = step.departmentName || '未知部門';
      const roleName = step.roleName || '未知角色';
      return `${deptName} - ${roleName}`;
    } else if (step.approverType === 'specific_user') {
      return step.userName || this.i18nService.translate('form.approval_flow.specific_user');
    }

    return '';
  }

  /**
   * 取得當前使用者的顯示名稱
   * 直接從 authService 的 localStorage 取得
   */
  getCurrentUserDisplayName(): string {
    // 直接從 authService 取得 localStorage 中的 username
    const userInfo = this.authService.getUserInfo();
    return userInfo.username || this.currentUserId;
  }

  // 新增：自訂表單相關方法

  /**
   * 檢查步驟是否有自訂表單
   */
  hasCustomForm(stepIndex: number): boolean {
    // 檢查 approvalFlow 資料結構
    if (!this.approvalFlow || !this.approvalFlow[stepIndex]) {
      return false;
    }

    const step = this.approvalFlow[stepIndex] as any;
    const hasCustomForm = step.customFormItems && step.customFormItems.length > 0;

    return hasCustomForm;
  }

  /**
   * 取得步驟的自訂表單設定
   */
  getStepCustomFormFields(stepIndex: number): FormFieldConfig[] {
    if (!this.approvalFlow || !this.approvalFlow[stepIndex]) {
      return [];
    }

    const step = this.approvalFlow[stepIndex] as any;
    const customFormItems = step.customFormItems || [];

    return customFormItems;
  }

  /**
   * 取得自訂表單欄位的值
   */
  getCustomFormFieldValue(stepIndex: number, fieldName: string): any {
    if (!this.customFormData[stepIndex]) {
      this.customFormData[stepIndex] = {};
    }
    return this.customFormData[stepIndex][fieldName] || '';
  }

  /**
   * 更新自訂表單欄位的值
   */
  updateCustomFormFieldValue(stepIndex: number, fieldName: string, value: any): void {
    if (!this.customFormData[stepIndex]) {
      this.customFormData[stepIndex] = {};
    }
    this.customFormData[stepIndex][fieldName] = value;

    // 觸發原有的變更事件處理
    this.onCustomFormDataChange(stepIndex, this.customFormData[stepIndex]);
  }

  /**
   * 自訂表單資料變更處理
   */
  onCustomFormDataChange(stepIndex: number, formData: any): void {
    this.customFormData[stepIndex] = { ...formData };

    // 更新表單驗證狀態（這裡可以加入更複雜的驗證邏輯）
    this.customFormValidation[stepIndex] = this.validateCustomFormData(stepIndex, formData);
  }

  /**
   * 驗證自訂表單資料（可擴展）
   */
  private validateCustomFormData(stepIndex: number, formData: any): boolean {
    // 測試模式：總是回傳 true
    if (this.isTestMode) {
      return true;
    }

    const formFields = this.getStepCustomFormFields(stepIndex);

    // 檢查必填欄位
    for (const field of formFields) {
      if (field.required) {
        if (field.type === 'date-range') {
          // 日期範圍欄位需要檢查開始和結束日期
          const startValue = formData[field.name + '_start'];
          const endValue = formData[field.name + '_end'];
          if (!startValue || !endValue || startValue.trim() === '' || endValue.trim() === '') {
            return false;
          }
        } else {
          // 一般欄位檢查
          if (!formData[field.name] || formData[field.name].trim() === '') {
            return false;
          }
        }
      }
    }

    return true;
  }

  /**
   * 檢查自訂表單是否有效（用於控制簽核按鈕）
   */
  isCustomFormValid(stepIndex: number): boolean {
    // 測試模式：總是回傳 true，讓使用者可以正常簽核
    if (this.isTestMode) {
      return true;
    }

    // 如果沒有自訂表單，視為有效
    if (!this.hasCustomForm(stepIndex)) {
      return true;
    }

    // 檢查表單驗證狀態
    const isValid = this.customFormValidation[stepIndex] ?? true;
    return isValid;
  }

  /**
   * 處理自訂表單資料以便提交（合併日期範圍欄位）
   */
  private processCustomFormDataForSubmit(stepIndex: number): any {
    const formData = { ...this.customFormData[stepIndex] };
    const formFields = this.getStepCustomFormFields(stepIndex);

    // 處理日期範圍欄位，將 _start 和 _end 合併成單一物件
    for (const field of formFields) {
      if (field.type === 'date-range') {
        const startValue = formData[field.name + '_start'];
        const endValue = formData[field.name + '_end'];

        if (startValue || endValue) {
          formData[field.name] = {
            start: startValue || '',
            end: endValue || ''
          };
        }

        // 移除分離的欄位
        delete formData[field.name + '_start'];
        delete formData[field.name + '_end'];
      }
    }

    return formData;
  }

  /**
   * 檢查是否有自訂表單資料
   */
  hasCustomFormData(customFormData: any): boolean {
    if (!customFormData || typeof customFormData !== 'object') {
      return false;
    }

    return Object.keys(customFormData).some(key =>
      customFormData[key] !== null &&
      customFormData[key] !== undefined &&
      customFormData[key] !== ''
    );
  }

  /**
   * TrackBy function for step iteration to optimize ngFor performance
   */
  trackByStepIndex(index: number, item: any): number {
    return index;
  }

  /**
   * TrackBy function for form field iteration to optimize ngFor performance
   */
  trackByFieldName(index: number, field: FormFieldConfig): string {
    return field.name || field.id || index.toString();
  }

  /**
   * TrackBy function for approver list iteration
   */
  trackByApproverId(index: number, approver: any): string {
    return approver.id || approver.name || index.toString();
  }

  /**
   * TrackBy function for select options
   */
  trackByOptionValue(index: number, option: any): string {
    return option.value || index.toString();
  }

  /**
   * 創建測試用的簽核流程資料（包含自訂表單）
   * 僅在測試模式下使用
   */
  private createTestApprovalFlow(): void {
    if (!this.isTestMode) return;

    this.approvalFlow = [
      {
        stepName: '品質檢驗',
        level: 1,
        approverType: 'role_based',
        departmentName: '品質部',
        roleName: '品質檢驗員',
        userId: this.currentUserId, // 設為當前用戶，確保可以簽核
        userName: this.currentUserName,
        // 新增自訂表單範例
        customFormItems: [
          {
            type: 'textarea',
            label: '檢驗意見',
            id: 'inspection_comment',
            name: 'inspection_comment',
            placeholder: '請填寫檢驗意見...',
            required: true
          },
          {
            type: 'text-input',
            label: '檢驗編號',
            id: 'inspection_number',
            name: 'inspection_number',
            placeholder: '請輸入檢驗編號',
            required: false
          },
          {
            type: 'select',
            label: '檢驗結果',
            id: 'inspection_result',
            name: 'inspection_result',
            options: [
              { value: 'pass', label: '合格' },
              { value: 'fail', label: '不合格' },
              { value: 'conditional', label: '有條件通過' }
            ],
            required: true
          }
        ]
      },
      {
        stepName: '主管審核',
        level: 2,
        approverType: 'role_based',
        departmentName: '管理部',
        roleName: '部門主管',
        userId: this.currentUserId, // 設為當前用戶，確保可以簽核
        userName: this.currentUserName,
        // 第二步驟的自訂表單
        customFormItems: [
          {
            type: 'textarea',
            label: '主管意見',
            id: 'manager_comment',
            name: 'manager_comment',
            placeholder: '請填寫審核意見...',
            required: true
          },
          {
            type: 'date',
            label: '預計完成日期',
            id: 'expected_date',
            name: 'expected_date',
            required: false
          }
        ]
      }
    ] as any[];

    // 創建對應的簽核實例
    this.createApprovalInstanceFromFlow();
  }

  /**
   * 從簽核流程步驟中提取自訂表單資料
   */
  private extractCustomFormDataFromFlow(flowStep: any): any {
    const customFormData: any = {};

    if (flowStep.customFormItems && Array.isArray(flowStep.customFormItems)) {
      flowStep.customFormItems.forEach((item: any) => {
        if (item.name && item.value !== undefined) {
          customFormData[item.name] = item.value;
        }
      });
    }

    return customFormData;
  }

  /**
   * 取得使用者電子郵件地址
   */
  private getUserEmails(userIds: string[]): Promise<any> {
    const companyId = environment.companyid;
    const apiUrl = environment.apiUrl;

    if (!companyId || !apiUrl) {
      console.error('Missing companyId or apiUrl in environment');
      return Promise.resolve({ success: false, emails: [], error: 'Missing environment configuration' });
    }

    if (!userIds || userIds.length === 0) {
      console.error('UserIds array is empty or null');
      return Promise.resolve({ success: false, emails: [], error: 'No user IDs provided' });
    }

    console.log('Fetching emails for user IDs:', userIds);

    return this.http.post(`${apiUrl}/api/users/get-emails-by-ids`, {
      companyid: companyId,
      ids: userIds
    }).toPromise().catch(error => {
      console.error('Error fetching user emails:', error);
      return { success: false, emails: [], error: error.message || 'API call failed' };
    });
  }

  /**
   * 發送簽核通知郵件
   */
  private async sendApprovalNotification(
    recipientEmails: string[],
    approvalInfo: any,
    locale: 'zh-TW' | 'ja-JP' | 'en-US',
    recipientNames?: Map<string, string>  // 📧 氏名マッピングを追加
  ): Promise<void> {
    try {
      const result = await this.emailService.sendApprovalNotification(
        recipientEmails,
        approvalInfo,
        locale,
        recipientNames  // 📧 氏名マッピングを渡す
      ).toPromise();

      console.log('簽核通知郵件發送結果:', result);
    } catch (error) {
      console.error('簽核通知郵件發送失敗:', error);
      throw error;
    }
  }

  /**
   * 生成任務連結 URL
   * JSAI-509 & JSAI-799: 生成簽核任務連結,包含 id、dep_id 和 formId 參數
   */
  private generateTaskUrl(): string {
    const baseUrl = window.location.origin;

    // 🔧 根據 group_id 設定決定使用哪個元件
    const groupId = environment.group_id || 'JSAI';
    const componentName = (groupId === 'IAD')
      ? 'FormApprovalListComponent'    // IAD → 承認一覧
      : 'workApplicationComponent';    // JSAI → 施工申請

    // 從當前路由取得 assistantNo 和 dep_id
    const queryParams = this.router.routerState.snapshot.root.queryParams;
    const assistantNo = queryParams['id'] || '';
    const depId = queryParams['dep_id'] || '';

    // JSAI-799: 生成指向簽核視窗的 URL
    // 格式: /assistant?id={assistantNo}&dep_id={depId}&component={componentName}&formId={formId}&recordId={recordId}&openApproval=true&stepIndex={stepIndex}
    // componentName: IADの場合はFormApprovalListComponent、JSAIの場合はworkApplicationComponent
    // formId: 表單配置 ID
    // recordId: 表單記錄 ID
    // openApproval=true: 標記要打開簽核視窗而不是編輯視窗
    // stepIndex: 要顯示的簽核步驟索引
    if (assistantNo && this.id) {
      let url = `${baseUrl}/assistant?id=${assistantNo}`;

      // 加入 dep_id（如果有）
      if (depId) {
        url += `&dep_id=${depId}`;
      }

      // 加入 component 參數（動的に決定）
      url += `&component=${componentName}`;

      // 加入 formId（表單配置 ID）
      if (this.formId) {
        url += `&formId=${this.formId}`;
      }

      // 加入 recordId（表單記錄 ID）
      url += `&recordId=${this.id}`;

      // 加入 openApproval 標記（表示要打開簽核視窗）
      url += `&openApproval=true`;

      // 加入 stepIndex（當前簽核步驟索引）
      const currentStepIndex = this.approvalInstance?.currentStepIndex ?? 0;
      url += `&stepIndex=${currentStepIndex}`;

      console.log('✅ 生成審核連結:', url, {
        recordId: this.id,
        openApproval: true,
        stepIndex: currentStepIndex
      });
      return url;
    } else if (this.id) {
      // 如果沒有 assistantNo，使用簡化格式
      console.warn('⚠️ 缺少 assistantNo，使用簡化連結格式');
      return `${baseUrl}/assistant?component=${componentName}&recordId=${this.id}`;
    } else {
      console.warn('⚠️ 無法生成連結：缺少 assistantNo 或 formDataId');
      // フォールバック: emailServiceに応じた承認画面に遷移
      return `${baseUrl}/assistant?component=${componentName}`;
    }
  }

  /**
   * 檢查欄位類型是否被支援
   */
  isSupportedFieldType(fieldType: string): boolean {
    const supportedTypes = [
      'text-input',
      'textarea',
      'select',
      'number-input',
      'date',
      'date-range'
    ];

    const isSupported = supportedTypes.includes(fieldType);

    // 如果不支援，在控制台輸出警告訊息
    if (!isSupported) {
      const warningMessage = this.i18nService.translate('approval.status.console_unsupported_warning', { type: fieldType });
      const suggestion = this.i18nService.translate('approval.status.console_suggestion');

      console.warn(warningMessage, {
        type: fieldType,
        supportedTypes: supportedTypes,
        suggestion: suggestion
      });
    }

    return isSupported;
  }

  /**
   * 取得當前語言值（同步），並確保是支援的語系
   */
  private getCurrentLanguageSync(): 'zh-TW' | 'en-US' | 'ja-JP' {
    const currentLang = this.i18nService.currentLangValue;

    // 確保回傳的語系是郵件服務支援的語系
    switch (currentLang) {
      case 'ja-JP':
        return 'ja-JP';
      case 'en-US':
        return 'en-US';
      case 'zh-TW':
      default:
        return 'zh-TW'; // 預設使用繁體中文
    }
  }

  // ========== 新增：拒絕/退回相關方法 ==========

  /**
   * 初始化拒絕/退回選項
   */
  private initializeRejectOptions(stepIndex: number) {
    this.availableReturnSteps = this.getAvailableReturnSteps(stepIndex);
    this.rejectFormData = {
      rejectType: 'complete_reject',
      returnToStepId: '',
      rejectReason: ''
    };
  }

  /**
   * 取得可退回的階段選項
   */
  private getAvailableReturnSteps(currentStepIndex: number): any[] {
    const actualIndex = this.getActualStepIndex(currentStepIndex);

    if (!this.approvalFlow || actualIndex <= 0) {
      return [];
    }

    return this.approvalFlow
      .slice(0, actualIndex)
      .map((step, index) => ({
        stepId: step.stepDefinitionId,
        stepName: step.stepName,
        stepIndex: index,
        actualIndex: index,  // ⭐ 新增：實際的步驟索引（與 stepIndex 相同，為了向後相容）
        displayText: `第${index + 1}關 - ${step.stepName}`
      }));
  }

  /**
   * 驗證拒絕/退回表單
   */
  private validateRejectForm(): boolean {
    // 檢查拒絕原因
    if (!this.rejectFormData.rejectReason.trim()) {
      const errorMsg = this.i18nService.translate('approval.reject.validation.reason_required');
      console.error(errorMsg);
      // TODO: 可以在這裡加入 Toast 通知
      return false;
    }

    // 檢查退回階段選擇
    if (this.rejectFormData.rejectType === 'return_to_step' && !this.rejectFormData.returnToStepId) {
      const errorMsg = this.i18nService.translate('approval.reject.validation.step_required');
      console.error(errorMsg);
      return false;
    }

    return true;
  }

  /**
   * 確認拒絕/退回操作
   */
  confirmReject(stepIndex: number) {
    if (!this.validateRejectForm()) {
      return;
    }

    // 執行對應的拒絕邏輯
    this.performRejectAction(stepIndex);
    this.showRejectOptions = false;
    this.currentRejectStepIndex = null;

    // 將原因填入原有的 comment 欄位以保持向下相容
    this.comment = this.rejectFormData.rejectReason;
    this.signature = null;
  }

  /**
   * 執行拒絕/退回動作
   */
  private performRejectAction(stepIndex: number) {
    if(!this.stepDefinitionId || !this.approvalInstance) return;

    const currentStep = this.approvalInstance.steps.find(step =>
      step.stepDefinitionId === this.stepDefinitionId
    ) as ApprovalStepInstanceWithAction;

    if (!currentStep) {
      console.error('找不到對應的步驟定義ID:', this.stepDefinitionId);
      return;
    }

    this.actionInProgress = true;
    currentStep.actionResult = null;

    // 🔑 新增：將拒絕動作加入歷史記錄（深拷貝完整的已完成步驟）
    this.addRejectActionToHistory(stepIndex);

    // 設置基本的拒絕資訊
    currentStep.status = 'rejected';
    currentStep.comment = this.rejectFormData.rejectReason;
    currentStep.signature = this.signature?.imageData || null;
    currentStep.actionDate = new Date();

    // 新增：退回類型資訊
    (currentStep as any).rejectType = this.rejectFormData.rejectType;
    (currentStep as any).returnToStepId = this.rejectFormData.returnToStepId;
    (currentStep as any).rejectReason = this.rejectFormData.rejectReason;

    // 取得實際的步驟索引
    const actualStepIndex = this.getActualStepIndex(stepIndex);

    // 如果有自訂表單資料，也要包含進去
    if (this.customFormData[actualStepIndex]) {
      (currentStep as any).customFormData = this.processCustomFormDataForSubmit(actualStepIndex);
    }

    // 設置操作結果
    const actionText = this.getRejectActionText();
    currentStep.actionResult = {
      success: true,
      message: this.i18nService.translate('approval.status.action_completed', { action: actionText })
    };

    // 更新整個 approvalInstance 的狀態
    if (this.approvalInstance) {
      this.approvalInstance.stepDefinitionId = this.stepDefinitionId;

      // 🔑 修改：根據退回類型決定流程狀態和步驟重置
      switch (this.rejectFormData.rejectType) {
        case 'complete_reject':
          // 完全拒絕：整個流程結束，保持 rejected 狀態
          this.approvalInstance.status = 'rejected';
          this.approvalInstance.completedAt = new Date();
          break;

        case 'return_to_submitter':
          // 退回給提交人：清空簽核流程，等待提交人重新填寫並提交
          // ⚠️ 重要：不是重置步驟，而是清空整個簽核流程
          // 這樣可以確保提交人重新提交時會重新產生簽核流程
          this.approvalInstance.status = 'returned';  // 使用 'returned' 狀態表示需要重新填寫
          this.approvalInstance.currentStepIndex = -1;  // 設為 -1 表示尚未開始簽核
          this.approvalInstance.steps = [];  // 清空步驟陣列
          // 不設置 completedAt，因為流程還未結束
          console.log('✅ 已清空簽核流程，等待提交人重新填寫');
          break;

        case 'return_to_step':
          // 退回到指定階段：重置從指定階段到當前階段的所有步驟
          const targetStepId = this.rejectFormData.returnToStepId;
          this.resetStepsForResubmission('partial', targetStepId);
          // 設置流程從指定階段重新開始
          const targetStep = this.availableReturnSteps.find(step => step.stepId === targetStepId);
          if (targetStep) {
            this.approvalInstance.status = 'pending';
            this.approvalInstance.currentStepIndex = targetStep.stepIndex;
            this.approvalInstance.steps[targetStep.stepIndex].status = 'in_progress';
          }
          break;
      }

      // 添加歷史記錄到簽核實例中
      if (this.approvalHistory.length > 0) {
        (this.approvalInstance as any).history = [...this.approvalHistory];
      }

      this.approvalInstance.updatedAt = new Date();
    }

    console.log("完整的拒絕/退回資料:", {
      stepDefinitionId: currentStep.stepDefinitionId,
      rejectType: this.rejectFormData.rejectType,
      returnToStepId: this.rejectFormData.returnToStepId,
      rejectReason: this.rejectFormData.rejectReason,
      status: currentStep.status,
      comment: currentStep.comment,
      signature: currentStep.signature,
      customFormData: (currentStep as any).customFormData,
      actionDate: currentStep.actionDate,
      approvalInstanceStatus: this.approvalInstance?.status,
      historyCount: this.approvalHistory.length
    });

    // 🔑 特殊處理：退回給提交人時不需要提取當前步驟（因為已清空）
    let currentStepData: any;

    if (this.rejectFormData.rejectType === 'return_to_submitter') {
      // 退回給提交人：直接使用整個 approvalInstance（steps 已清空）
      currentStepData = {
        id: this.approvalInstance.id,
        flowDefinitionId: this.approvalInstance.flowDefinitionId,
        formId: this.approvalInstance.formId,
        formTypeId: this.approvalInstance.formTypeId,
        initiator: this.approvalInstance.initiator,
        status: this.approvalInstance.status, // 'returned'
        currentStepIndex: this.approvalInstance.currentStepIndex, // -1
        all_steps: this.approvalInstance.steps, // []
        history: (this.approvalInstance as any).history || this.approvalHistory || [],
        createdAt: this.approvalInstance.createdAt,
        updatedAt: this.approvalInstance.updatedAt
      };
      console.log('✅ 退回給提交人，使用空的簽核流程資料');
    } else {
      // 其他拒絕類型：正常提取當前簽核階段的資料
      currentStepData = this.extractCurrentStepData(this.approvalInstance);
    }

    // ✅ 提交簽核，並傳入 'reject' 動作類型
    // submitApproval 會自動呼叫 sendRejectNotification()
    this.submitApproval(currentStepData, 'reject');

    // ❌ 移除重複的通知呼叫（現在由 submitApproval 統一處理）
    // this.sendRejectNotification(stepIndex);

    this.actionInProgress = false;
    this.cdr.markForCheck();
  }

  /**
   * 🔑 新增：將拒絕動作加入歷史記錄
   * 深拷貝所有已完成的步驟，保存動態表單資料
   */
  private addRejectActionToHistory(currentStepIndex: number): void {
    if (!this.approvalInstance) return;

    const actualCurrentIndex = this.getActualStepIndex(currentStepIndex);
    const rejectedFromStep = actualCurrentIndex;
    const timestamp = new Date().toISOString();

    // 🔑 修正：確保取得正確的使用者資訊
    const currentUserInfo = this.authService.getUserInfo();
    const userId = this.currentUserId || currentUserInfo?.userid || '';
    const userName = this.currentUserName || currentUserInfo?.username || userId;

    console.log('🔍 使用者資訊檢查:', {
      currentUserId: this.currentUserId,
      currentUserName: this.currentUserName,
      fromAuthService: currentUserInfo,
      finalUserId: userId,
      finalUserName: userName
    });

    // 建立此次拒絕動作的歷史記錄
    const rejectAction = {
      actionType: 'reject',
      rejectType: this.rejectFormData.rejectType,
      rejectedFromStepIndex: rejectedFromStep,
      rejectedFromStepName: this.approvalInstance.steps[rejectedFromStep]?.name || '',
      rejectedBy: {
        userId: userId,
        userName: userName,
        rejectedAt: timestamp
      },
      rejectReason: this.rejectFormData.rejectReason,
      returnToStepId: this.rejectFormData.returnToStepId,
      returnToStepName: this.rejectFormData.rejectType === 'return_to_step' ?
        this.availableReturnSteps.find(step => step.stepId === this.rejectFormData.returnToStepId)?.stepName : null,

      // 🔑 核心：深拷貝所有已完成的步驟（完整保存動態表單資料）
      completedSteps: this.deepCopyCompletedSteps(rejectedFromStep)
    };

    // 將此次拒絕動作加入歷史記錄
    this.approvalHistory.push(rejectAction);

    console.log('✅ 拒絕動作已加入歷史記錄:', {
      actionType: rejectAction.actionType,
      rejectType: rejectAction.rejectType,
      rejectedFromStep: rejectAction.rejectedFromStepName,
      completedStepsCount: rejectAction.completedSteps.length,
      historyTotalCount: this.approvalHistory.length
    });
  }

  /**
   * 🔑 深拷貝已完成的步驟（包含所有動態表單資料）
   */
  private deepCopyCompletedSteps(rejectedFromStepIndex: number): any[] {
    if (!this.approvalInstance) return [];

    const completedSteps: any[] = [];

    // 複製從開始到被拒絕步驟（包含）的所有已完成步驟
    for (let i = 0; i <= rejectedFromStepIndex; i++) {
      const step = this.approvalInstance.steps[i];

      // 只有已完成的步驟才需要保存到歷史中
      if (step.status === 'approved' || step.status === 'rejected') {
        // 🔑 深拷貝整個步驟物件，確保動態表單資料完整保存
        const stepCopy = {
          // 基本步驟資訊
          stepDefinitionId: step.stepDefinitionId,
          name: step.name,
          order: step.order,
          status: step.status,
          comment: step.comment,
          signature: step.signature,
          actionDate: step.actionDate,

          // 簽核人資訊
          approver: {
            userId: step.approver.userId,
            userName: step.approver.userName,
            roleId: step.approver.roleId,
            roleName: step.approver.roleName,
            departmentId: step.approver.departmentId,
            departmentName: step.approver.departmentName
          },

          // 🔑 關鍵：完整複製自訂表單資料
          customFormData: this.deepCopyObject((step as any).customFormData || {}),

          // 從 approvalFlow 中取得原始的 customFormItems 定義
          originalCustomFormItems: this.getOriginalCustomFormItems(i),

          // 保存其他可能的動態欄位
          rejectType: (step as any).rejectType,
          returnToStepId: (step as any).returnToStepId,
          rejectReason: (step as any).rejectReason,
          actionResult: (step as any).actionResult
        };

        completedSteps.push(stepCopy);
      }
    }

    console.log(`📋 深拷貝完成，共保存 ${completedSteps.length} 個已完成步驟到歷史記錄`);
    return completedSteps;
  }

  /**
   * 🔑 取得原始的自訂表單項目定義（用於歷史重現）
   */
  private getOriginalCustomFormItems(stepIndex: number): any[] {
    if (!this.approvalFlow || !this.approvalFlow[stepIndex]) {
      return [];
    }

    const step = this.approvalFlow[stepIndex] as any;
    return step.customFormItems ? JSON.parse(JSON.stringify(step.customFormItems)) : [];
  }

  /**
   * 🔑 深拷貝物件（避免引用問題）
   */
  private deepCopyObject(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.deepCopyObject(item));
    }

    const copy: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        copy[key] = this.deepCopyObject(obj[key]);
      }
    }

    return copy;
  }

  /**
   * 🔑 重置步驟狀態以供重新提交
   */
  private resetStepsForResubmission(resetType: 'all' | 'partial', targetStepId?: string): void {
    if (!this.approvalInstance) return;

    const actualCurrentIndex = this.getActualStepIndex(this.currentRejectStepIndex || 0);

    switch (resetType) {
      case 'all':
        // 退回給提交人：重置所有步驟
        this.approvalInstance.steps.forEach((step, index) => {
          if (index === 0) {
            step.status = 'in_progress'; // 第一步重新開始
          } else {
            step.status = 'pending'; // 其他步驟等待
          }
          // 清除之前的簽核記錄（但保留在歷史中）
          step.comment = '';
          step.signature = null;
          step.actionDate = null;
          (step as any).customFormData = {};
        });
        console.log('✅ 已重置所有步驟狀態為 pending/in_progress');
        break;

      case 'partial':
        // 退回到指定階段：重置從指定階段到當前階段
        if (!targetStepId) return;

        const targetStep = this.availableReturnSteps.find(step => step.stepId === targetStepId);
        if (!targetStep) return;

        const targetStepIndex = targetStep.stepIndex;

        // 重置從目標階段到當前階段的所有步驟
        for (let i = targetStepIndex; i <= actualCurrentIndex; i++) {
          const step = this.approvalInstance.steps[i];
          if (i === targetStepIndex) {
            step.status = 'in_progress'; // 目標階段重新開始
          } else {
            step.status = 'pending'; // 後續階段等待
          }
          // 清除之前的簽核記錄（但保留在歷史中）
          step.comment = '';
          step.signature = null;
          step.actionDate = null;
          (step as any).customFormData = {};
        }

        console.log(`✅ 已重置步驟 ${targetStepIndex} 到 ${actualCurrentIndex} 的狀態`);
        break;
    }
  }

  /**
   * 取得拒絕動作的文字描述
   */
  private getRejectActionText(): string {
    switch (this.rejectFormData.rejectType) {
      case 'complete_reject':
        return this.i18nService.translate('approval.reject.actions.complete_reject');
      case 'return_to_step':
        const stepName = this.availableReturnSteps.find(step =>
          step.stepId === this.rejectFormData.returnToStepId
        )?.stepName || '';
        return this.i18nService.translate('approval.reject.actions.return_to_step', { stepName });
      case 'return_to_submitter':
        return this.i18nService.translate('approval.reject.actions.return_to_submitter');
      default:
        return this.i18nService.translate('approval.status.reject');
    }
  }

  /**
   * 選擇拒絕類型
   */
  selectRejectType(type: 'complete_reject' | 'return_to_step' | 'return_to_submitter') {
    this.rejectFormData.rejectType = type;
    // 如果選擇的不是退回到指定階段，則清空階段選擇
    if (type !== 'return_to_step') {
      this.rejectFormData.returnToStepId = '';
    }
  }

  /**
   * 取消拒絕/退回操作
   */
  cancelReject() {
    this.showRejectOptions = false;
    this.currentRejectStepIndex = null;
    // 重置表單數據
    this.rejectFormData = {
      rejectType: 'complete_reject',
      returnToStepId: '',
      rejectReason: ''
    };
  }

  /**
   * 關閉拒絕/退回對話框 (保留向下相容)
   */
  closeRejectModal() {
    this.cancelReject();
  }

  /**
   * TrackBy function for step selection dropdown
   */
  trackByStepId(index: number, step: any): string {
    return step.stepId || index.toString();
  }

  /**
   * 發送完成通知給申請人
   * 當所有簽核關卡都完成時呼叫
   */
  private async sendCompletionNotificationToSubmitter(): Promise<void> {
    try {
      // 1. 先從 approvalInstance 取得申請人 ID
      let submitterId = this.approvalInstance?.initiator?.userId;
      let formName = '';  // 🔧 修正：初始值改為空字串，才能觸發查詢
      let submitterName = this.approvalInstance?.initiator?.userName || '未知使用者';

      // 2. 嘗試從表單資料查詢（如果缺少必要資訊）
      const dataId = this.id || this.formId;  // 嘗試兩個 ID
      if ((!submitterId || !formName) && dataId) {
        console.log('📋 嘗試從表單資料查詢提交人資訊，表單資料 ID:', dataId);
        try {
          const apiUrl = environment.apiUrl;
          const formDataResponse = await this.http.get<any>(
            `${apiUrl}/api/form-manager/get-form-data`,
            {
              params: {
                companyid: environment.companyid,
                id: dataId
              }
            }
          ).toPromise();

          console.log('📋 完成通知 API 回應:', formDataResponse);

          if (formDataResponse && formDataResponse.success && formDataResponse.data) {
            submitterId = submitterId || formDataResponse.data.created_by;
            // 🔧 欄位名稱可能是 formName 或 componentname
            formName = formDataResponse.data.formName ||
                      formDataResponse.data.componentname ||
                      formName;
            console.log('✅ 從表單資料取得提交人資訊:', { submitterId, formName });
          } else {
            console.warn('⚠️ API 回應失敗或沒有資料');
          }
        } catch (error) {
          console.error('❌ 查詢表單資料失敗:', error);
        }
      } else if (!dataId) {
        console.warn('⚠️ 無 this.id 也無 this.formId，無法查詢表單資料');
      }

      // 3. 最後檢查：如果還是沒有 formName，使用預設值
      if (!formName) {
        formName = '未命名表單';
        console.warn('⚠️ 無法取得表單名稱，使用預設值');
      }

      // 4. 驗證是否有申請人 ID
      if (!submitterId) {
        console.warn('⚠️ 無法取得申請人 ID，無法發送完成通知');
        return;
      }

      // 4.5 查詢申請人的 email、username 和 fullName
      let submitterEmail: string | null = null;
      let submitterDisplayName: string = '';
      try {
        console.log('📧 查詢申請人資訊，userId:', submitterId);
        const apiUrl = environment.apiUrl;
        const userResponse = await this.http.get<any>(
          `${apiUrl}/api/users/get-user-email`,
          {
            params: {
              companyid: environment.companyid,
              userid: submitterId
            }
          }
        ).toPromise();

        if (userResponse && userResponse.success && userResponse.email) {
          submitterEmail = userResponse.email;
          // 優先使用 fullName，如果沒有則使用 username，都沒有則使用 email 前綴
          submitterDisplayName = userResponse.fullName || userResponse.username || userResponse.email.split('@')[0];
          console.log('✅ 成功取得申請人資訊:', {
            email: submitterEmail,
            displayName: submitterDisplayName
          });
        } else {
          console.warn('⚠️ API 回應失敗或沒有 email');
        }
      } catch (error) {
        console.error('❌ 查詢申請人資訊失敗:', error);
      }

      // 如果沒有 email，無法發送通知
      if (!submitterEmail) {
        console.warn('⚠️ 無法取得申請人 email，無法發送完成通知');
        return;
      }

      // 5. 取得當前語系（提早執行，因為 getApprovalHistorySummary 需要）
      const currentLocale = this.getCurrentLanguageSync();

      // 5. 準備完成通知資料
      const completionData = {
        formId: this.formId || this.id || 'unknown',
        formName: formName,
        submitterName: submitterDisplayName,  // 使用 fullName > username > email前綴
        totalSteps: this.approvalFlow?.length || 0,
        completedDate: new Date().toLocaleString(currentLocale === 'ja-JP' ? 'ja-JP' : currentLocale === 'en-US' ? 'en-US' : 'zh-TW'),
        formLink: `${window.location.origin}/form-viewer/${this.formId || this.id}`,
        approvalHistory: this.getApprovalHistorySummary(currentLocale)
      };

      console.log('✅ 準備發送完成通知:', {
        submitterId,
        submitterEmail,
        formName: completionData.formName,
        locale: currentLocale,
        totalSteps: completionData.totalSteps
      });

      // 6. 發送完成通知（使用 email 而不是 userId）
      this.emailService.sendCompletionNotification(
        submitterEmail,
        completionData,
        currentLocale
      ).subscribe({
        next: (response) => {
          console.log('✅ 完成通知已發送給申請人:', submitterEmail, response);
        },
        error: (error) => {
          console.error('❌ 發送完成通知失敗:', error);
          // 不拋出錯誤，避免影響簽核流程
        }
      });

    } catch (error) {
      console.error('❌ 發送完成通知時發生錯誤:', error);
      // 不拋出錯誤，避免影響簽核流程
    }
  }

  /**
   * 取得簽核歷程摘要（用於完成通知）
   * @param locale - 語言代碼
   */
  private getApprovalHistorySummary(locale: 'zh-TW' | 'ja-JP' | 'en-US' = 'zh-TW'): string {
    if (!this.approvalHistory || this.approvalHistory.length === 0) {
      // 根據語言返回不同的「無記錄」訊息
      switch (locale) {
        case 'ja-JP':
          return '承認記録がありません';
        case 'en-US':
          return 'No approval records';
        case 'zh-TW':
        default:
          return '無簽核記錄';
      }
    }

    const summary = this.approvalHistory
      .map((record, index) => {
        const stepNumber = index + 1;
        const approverName = record.approverName || (locale === 'ja-JP' ? '不明' : locale === 'en-US' ? 'Unknown' : '未知');

        // 根據語言顯示不同的動作文字
        let action: string;
        if (record.action === 'approved') {
          action = locale === 'ja-JP' ? '✅ 承認' : locale === 'en-US' ? '✅ Approved' : '✅ 核准';
        } else if (record.action === 'rejected') {
          action = locale === 'ja-JP' ? '❌ 却下' : locale === 'en-US' ? '❌ Rejected' : '❌ 拒絕';
        } else {
          action = locale === 'ja-JP' ? 'その他' : locale === 'en-US' ? 'Other' : '其他';
        }

        const time = record.timestamp ?
          new Date(record.timestamp).toLocaleString(locale === 'ja-JP' ? 'ja-JP' : locale === 'en-US' ? 'en-US' : 'zh-TW') :
          (locale === 'ja-JP' ? '不明な時刻' : locale === 'en-US' ? 'Unknown time' : '未知時間');

        // 根據語言返回不同格式
        if (locale === 'ja-JP') {
          return `第${stepNumber}段階：${approverName} - ${action} (${time})`;
        } else if (locale === 'en-US') {
          return `Step ${stepNumber}: ${approverName} - ${action} (${time})`;
        } else {
          return `第 ${stepNumber} 關：${approverName} - ${action} (${time})`;
        }
      })
      .join('\n');

    return summary;
  }
}
